import { Grid, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomModal from "../../../components/CustomModal/CustomModal";
import { printApi } from "../../../redux/actions/api";
import { a4PrintConfig } from "../../../util/commonConfig";
import {
  getPetShopRecordBasedType,
  getPharmacyRecordBasedType,
  getRightPackageUom,
  getRightSaluation,
  getTaxAndSubTotalAndTotal,
  getTreatmentRecordBasedType,
  removeDuplicateBracketText,
  reorderList,
  roundWithSignedDiff,
} from "../../../util/function";
import ContentToPrint from "../ContentToPrint/ContentToPrint";

const normalizeBillTabKey = (tab) => {
  const value = String(tab ?? "").toLowerCase();
  if (value === "pet shop" || value === "petshop") return "petshop";
  return value;
};

const resolveBillsToShow = (billsToShow = [], selectedBill = null) => {
  const normalized = (billsToShow ?? []).map((tab) => normalizeBillTabKey(tab));
  const billTab = selectedBill?.tab
    ? normalizeBillTabKey(selectedBill.tab)
    : null;
  if (billTab) {
    return normalized.filter((tab) => tab === billTab);
  }
  return normalized;
};

/** Self-contained invoice print styles — matches on-screen preview without loading app bundles. */
const BILLING_INVOICE_PRINT_CSS = `
  .billing-print-root {
    background: #fff;
    color: #000;
    box-sizing: border-box;
    font-family: "Montserrat", Arial, sans-serif;
  }
  .billing-print-root * { box-sizing: border-box; }
  .billing-print-root .MuiTypography-root {
    margin: 0;
    font-family: "Montserrat", Arial, sans-serif !important;
    line-height: 1.43;
  }
  .flex-row { display: flex; flex-direction: row; }
  .flex-row, .flex-row-ali-cen {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  .flex-column { display: flex; flex-direction: column; }
  .flex-center { display: flex; justify-content: center; align-items: center; }
  .flex-end { display: flex; justify-content: flex-end; }
  .flex1-end {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
  }
  .flex1 { flex: 1; }
  .flex-row-between {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
  .flex-row-wrap { display: flex; flex-direction: row; flex-wrap: wrap; }
  .ali-itm-flx-end { display: flex; align-items: flex-end; }
  .billing-print-root .flex1-end .flex-column > div + div { margin-top: 5px; }
  .w5Per { width: 5%; }
  .w7Per { width: 7%; }
  .w10Per { width: 10%; }
  .w15Per { width: 15%; }
  .w18Per { width: 18%; }
  .w20Per { width: 20%; }
  .w25Per { width: 25%; }
  .w30Per { width: 30%; }
  .w35Per { width: 35%; }
  .w40Per { width: 40%; }
  .w45Per { width: 45% !important; }
  .w50Per { width: 50% !important; }
  .w55Per { width: 55% !important; }
  .w60Per { width: 60% !important; }
  .w70Per { width: 70%; }
  .w100Per { width: 100%; }
  .ph5 { padding-left: 5px; padding-right: 5px; }
  .ph10 { padding-left: 10px; padding-right: 10px; }
  .pl20 { padding-left: 20px; }
  .pr10 { padding-right: 10px; }
  .mt5 { margin-top: 5px; }
  .mt10 { margin-top: 10px; }
  .mt30 { margin-top: 30px; }
  .mv5 { margin-top: 5px; margin-bottom: 5px; }
  .mv20 { margin-top: 20px; margin-bottom: 20px; }
  .mb5 { margin-bottom: 5px; }
  .mb20 { margin-bottom: 20px; }
  .mr10 { margin-right: 10px; }
  .ml2 { margin-left: 2px; }
  .ml3 { margin-left: 3px; }
  .ml5 { margin-left: 5px; }
  .ml10 { margin-left: 10px !important; }
  .ml20 { margin-left: 20px !important; }
  .capitalize { text-transform: capitalize; }
  .font-bold {
    font-family: "Montserrat", Arial, sans-serif;
    font-weight: 700;
  }
  .font-medium {
    font-family: "Montserrat", Arial, sans-serif;
    font-weight: 500;
  }
  .font-regular {
    font-family: "Montserrat", Arial, sans-serif;
    font-weight: 400;
  }
  .txt-semi-bold {
    font-family: "Montserrat", Arial, sans-serif;
    font-weight: 600;
  }
  .txt-regular {
    font-family: "Montserrat", Arial, sans-serif;
    font-weight: 400;
  }
  .fs10 { font-size: 10px; }
  .fs12 { font-size: 12px; }
  .fs14 { font-size: 14px; }
  .fs16 { font-size: 16px; }
  .fs18 { font-size: 18px; }
  .fs30 { font-size: 30px; }
  .blue-color { color: #0054a6 !important; }
  .black { color: #000 !important; }
  .gray4 { color: #828282 !important; }
  .white-color { color: #fff !important; }
  .yellow-color { color: #fdb913 !important; }
  .green-color { color: #03992d !important; }
  .green2 { color: #339903 !important; }
  .red2 { color: #ff1d1dcc !important; }
  .orange-color { color: #f28c28 !important; }
  .gray-color { color: #a5b4cb !important; }
  .img-h75 { height: 75px; width: auto; max-width: 100%; object-fit: contain; }
  .h50img {
    height: 50px;
    width: 50px;
    background: #dfdede;
    border-radius: 4px;
  }
  .line { background-color: #0054a6; height: 3px; width: 100%; }
  .thin-line, .thin-line2 {
    background-color: #ccc;
    height: 1px;
    width: 100%;
    border: 0;
    margin: 0;
  }
  hr.thin-line, hr.thin-line2 {
    display: block;
    border: none;
  }
  .box-container {
    border: 1px solid #ccc;
    border-radius: 8px;
    width: 97%;
    margin: 20px 0 30px 20px;
  }
  .balancecontainer { background-color: #bfd0d4; padding: 5px; }
  .header-right-text2 {
    font-family: "Montserrat", Arial, sans-serif;
    font-weight: 400;
    color: #0054a6;
    font-size: 12px;
  }
  .txtstyle {
    color: #929fb1;
    font-size: 14px;
    font-family: "Montserrat", Arial, sans-serif;
    font-weight: 400;
  }
  .custom-table { border-collapse: collapse; width: 100%; }
  .custom-table th {
    border: none;
    border-bottom: 1px solid #ccc;
    padding: 10px;
    font-size: 12px;
    color: gray;
    text-align: left;
    font-family: "Montserrat", Arial, sans-serif;
    font-weight: 500;
  }
  .custom-table td {
    border: none;
    padding: 10px;
    font-size: 12px;
    font-family: "Montserrat", Arial, sans-serif;
    font-weight: 500;
    color: #000;
    text-align: left;
    vertical-align: top;
  }
  .billing-print-root .custom-table tbody tr.billing-invoice-line-row,
  .billing-print-root .custom-table tbody tr.billing-invoice-totals-row {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .billing-print-root .custom-table tbody tr.billing-invoice-line-row td,
  .billing-print-root .custom-table tbody tr.billing-invoice-totals-row td {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .billing-invoice-item-cell {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .billing-invoice-item-cell > * {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .billing-invoice-section-break {
    break-before: page;
    page-break-before: always;
  }
  .billing-invoice-table-footer {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .billing-payment-summary-section {
    break-inside: avoid;
    page-break-inside: avoid;
  }
`;

const BILLING_PRINT_OVERRIDES = `
  @media print {
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      height: auto !important;
      min-height: 0 !important;
      overflow: visible !important;
      background: #fff !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .billing-print-root,
    .billing-print-root * {
      visibility: visible !important;
      opacity: 1 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .billing-print-root:not(.billing-dotmatrix) {
      height: auto !important;
      min-height: 0 !important;
      max-height: none !important;
      overflow: visible !important;
      page-break-after: avoid !important;
      break-after: avoid-page !important;
    }
    .billing-print-root .MuiTypography-root.black { color: #000 !important; }
    .billing-print-root .MuiTypography-root.blue-color { color: #0054a6 !important; }
    .billing-print-root .MuiTypography-root.gray4 { color: #828282 !important; }
    .billing-print-root .MuiTypography-root.gray-color { color: #a5b4cb !important; }
    .billing-print-root .MuiTypography-root.green2 { color: #339903 !important; }
    .billing-print-root .MuiTypography-root.red2 { color: #ff1d1dcc !important; }
    .billing-print-root .MuiTypography-root.orange-color { color: #f28c28 !important; }
    .billing-print-root .MuiTypography-root.green-color { color: #03992d !important; }
    .billing-print-root .MuiTypography-root.yellow-color { color: #fdb913 !important; }
    .billing-print-root .MuiTypography-root.white-color { color: #fff !important; }
    .billing-print-root .line { background-color: #0054a6 !important; }
    .billing-print-root .thin-line,
    .billing-print-root .thin-line2,
    .billing-print-root hr.thin-line,
    .billing-print-root hr.thin-line2 {
      background-color: #ccc !important;
    }
    .billing-print-root .balancecontainer {
      background-color: #bfd0d4 !important;
    }
    .billing-print-root .balancecontainer .blue-color {
      color: #0054a6 !important;
    }
    .billing-print-root .custom-table thead {
      display: table-header-group !important;
    }
    .billing-print-root .custom-table thead tr {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .billing-print-root .custom-table thead th {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      font-weight: 600 !important;
      color: #828282 !important;
    }
    .billing-print-root .custom-table tbody tr.billing-invoice-line-row,
    .billing-print-root .custom-table tbody tr.billing-invoice-totals-row {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .billing-print-root .custom-table tbody tr.billing-invoice-line-row td,
    .billing-print-root .custom-table tbody tr.billing-invoice-totals-row td {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .billing-print-root .billing-invoice-item-cell,
    .billing-print-root .billing-invoice-item-cell > * {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .billing-print-root .billing-invoice-table-footer {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .billing-print-root .billing-payment-summary-section {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .billing-print-root .billing-payment-summary-section .custom-table {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .billing-print-root:not(.billing-dotmatrix) .page-break {
      display: none !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      page-break-before: auto !important;
      break-before: auto !important;
    }
    .billing-print-root:not(.billing-dotmatrix) .billing-invoice-section-break {
      break-before: page !important;
      page-break-before: always !important;
    }
    .billing-print-root:not(.billing-dotmatrix) > :last-child {
      page-break-after: avoid !important;
      break-after: avoid-page !important;
    }
  }
`;

const BILLING_A4_PRINT_STYLE = `${BILLING_INVOICE_PRINT_CSS}
  ${a4PrintConfig}
  ${BILLING_PRINT_OVERRIDES}
`;

let billingPrintFrame = null;
let cachedMontserratFontCss = null;
let billingPrintStylesInjected = false;

const extractMontserratFontFaceCss = () => {
  if (typeof document === "undefined") return "";
  const rules = [];
  for (const sheet of document.styleSheets) {
    try {
      for (const rule of sheet.cssRules ?? []) {
        if (
          rule instanceof CSSFontFaceRule &&
          /Montserrat/i.test(rule.cssText)
        ) {
          rules.push(rule.cssText);
        }
      }
    } catch {
      // Skip cross-origin stylesheets.
    }
  }
  return rules.join("\n");
};

const getMontserratFontFaceCss = () => {
  if (cachedMontserratFontCss !== null) return cachedMontserratFontCss;
  cachedMontserratFontCss = extractMontserratFontFaceCss();
  return cachedMontserratFontCss;
};

const cloneBillingPrintNode = (node) => {
  const clone = node.cloneNode(true);
  const sourceImgs = node.querySelectorAll("img");
  const cloneImgs = clone.querySelectorAll("img");
  cloneImgs.forEach((img, index) => {
    const source = sourceImgs[index];
    if (!source) return;
    img.decoding = "sync";
    img.loading = "eager";
    const src = source.currentSrc || source.src;
    if (src) img.src = src;
  });
  return clone;
};

const areBillingSourceImagesReady = (node) => {
  const imgs = node.querySelectorAll("img");
  if (!imgs.length) return true;
  return [...imgs].every(
    (img) => img.complete && (img.naturalWidth > 0 || img.naturalHeight > 0),
  );
};

const ensureBillingPrintFrameReady = (doc) => {
  const fontCss = getMontserratFontFaceCss();
  if (fontCss && !doc.getElementById("billing-print-font-face")) {
    const fontEl = doc.createElement("style");
    fontEl.id = "billing-print-font-face";
    fontEl.textContent = fontCss;
    doc.head.appendChild(fontEl);
  }

  let styleEl = doc.getElementById("billing-print-style");
  if (!styleEl) {
    styleEl = doc.createElement("style");
    styleEl.id = "billing-print-style";
    doc.head.appendChild(styleEl);
  }
  if (!billingPrintStylesInjected) {
    styleEl.textContent = BILLING_A4_PRINT_STYLE;
    billingPrintStylesInjected = true;
  }
};

const getBillingPrintDocument = () => {
  if (billingPrintFrame?.contentDocument) {
    return {
      iframe: billingPrintFrame,
      doc: billingPrintFrame.contentDocument,
    };
  }

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  Object.assign(iframe.style, {
    position: "fixed",
    width: "0",
    height: "0",
    border: "0",
    right: "0",
    bottom: "0",
    visibility: "hidden",
    pointerEvents: "none",
  });
  document.body.appendChild(iframe);
  billingPrintFrame = iframe;

  const doc = iframe.contentDocument;
  doc.open();
  doc.write(
    '<!DOCTYPE html><html><head><meta charset="utf-8"><title></title></head><body></body></html>',
  );
  doc.close();

  ensureBillingPrintFrameReady(doc);

  return { iframe, doc };
};

const syncBillingPrintContent = (node, doc) => {
  if (!node) return;
  doc.body.replaceChildren(cloneBillingPrintNode(node));
};

const warmBillingPrintFrame = (node) => {
  if (!node || typeof window === "undefined") return;
  const { doc } = getBillingPrintDocument();
  ensureBillingPrintFrameReady(doc);
  syncBillingPrintContent(node, doc);
};

const printBillingInvoice = (node, title = "Invoice") => {
  if (!node || typeof window === "undefined") return;

  const { iframe, doc } = getBillingPrintDocument();
  doc.title = title;
  ensureBillingPrintFrameReady(doc);
  syncBillingPrintContent(node, doc);

  const win = iframe.contentWindow;
  const invokePrint = () => {
    win.focus();
    win.print();
  };

  if (areBillingSourceImagesReady(node)) {
    invokePrint();
    return;
  }

  const images = Array.from(doc.images ?? []);
  if (!images.length) {
    invokePrint();
    return;
  }

  let printed = false;
  const finish = () => {
    if (printed) return;
    printed = true;
    invokePrint();
  };

  const deadline = window.setTimeout(finish, 100);

  let loaded = 0;
  const markLoaded = () => {
    loaded += 1;
    if (loaded >= images.length) {
      window.clearTimeout(deadline);
      finish();
    }
  };

  images.forEach((img) => {
    if (img.complete) markLoaded();
    else {
      img.onload = markLoaded;
      img.onerror = markLoaded;
    }
  });
};

/**
 * Text print layout aligned with Insurance Template Settings (Text / dot matrix).
 * Physical stock: 9.5in wide (incl. strips), 8in printable after tear-off, 11in tall per sheet.
 * 1 inch = 72 pt. PDF-oriented fields (font, page width) are not used for .txt.
 */
function mmToPt(mm) {
  return (Number(mm) * 72) / 25.4;
}

const TEXT_PRINT_TEMPLATE = Object.freeze({
  ptPerInch: 72,
  /** 11in sheet height → 792 pt (842pt would be ~A4) */
  pageHeightPt: 792,
  topMarginPt: 0,
  bottomMarginPt: 12,
  leftMarginPt: 0,
  rightMarginPt: 0,
  /** ~10 CPI on 8.5in printable (was 72). */
  textModeColumns: 137,
  textModeExtraLines: 3,
  footerEnabled: false,
  pageNumberEnabled: false,
  pageNumberPosition: "right",
  /** Used to convert bottom margin and page height into line counts */
  textLineHeightPt: 12,
  /** ~10 CPI on 8in printable; maps left margin pt to leading spaces */
  charsPerInchForMargin: 17,
});

/** Pharmacy dot-matrix .txt: 5mm L/R, 10mm top/bottom (Courier New in preview/print CSS). */
const PHARMACY_TEXT_PRINT_TEMPLATE = Object.freeze({
  ...TEXT_PRINT_TEMPLATE,
  topMarginPt: mmToPt(0),
  bottomMarginPt: mmToPt(0),
  leftMarginPt: mmToPt(0),
  rightMarginPt: mmToPt(0),
});

const PHARMACY_DOT_MATRIX_MARGINS = Object.freeze({
  horizontalMm: 5,
  verticalMm: 10,
  fontFamily: '"Courier New", Courier, monospace',
});

const DOT_MATRIX_STANDARD = Object.freeze({
  fullWidthIn: 9.5,
  printableWidthIn: 8.5,
  pageHeightIn: 11,
  sideStripIn: 0.5,
  sideStripRangeIn: "0.5-0.75",
  holePitchIn: 0.5,
  /** Treatment, pharmacy, and pet shop dot-matrix .txt preview/print (was 12px). */
  fontSizePx: 10,
});

function buildTextPrintLayout(template) {
  const S = template;
  const marginChars = Math.min(
    Math.max(
      0,
      Math.round((S.leftMarginPt / S.ptPerInch) * S.charsPerInchForMargin),
    ),
    Math.max(0, S.textModeColumns - 1),
  );
  const marginCharsRight = Math.min(
    Math.max(
      0,
      Math.round(
        ((S.rightMarginPt ?? 0) / S.ptPerInch) * S.charsPerInchForMargin,
      ),
    ),
    Math.max(0, S.textModeColumns - marginChars - 1),
  );
  const contentWidth = Math.max(
    1,
    S.textModeColumns - marginChars - marginCharsRight,
  );
  return {
    ...S,
    columns: S.textModeColumns,
    marginChars,
    marginCharsRight,
    contentWidth,
    indent: " ".repeat(marginChars),
  };
}

const TEXT_PRINT_LAYOUT = buildTextPrintLayout(TEXT_PRINT_TEMPLATE);
const PHARMACY_TEXT_PRINT_LAYOUT = buildTextPrintLayout(
  PHARMACY_TEXT_PRINT_TEMPLATE,
);

const TEXT_BLOCK_BREAK = "__VI_TEXT_BLOCK_BREAK__";
const GOODS_SOLD_NO_RETURN_FOOTER =
  "Goods once sold cannot be returned. Please check your items before leaving.";

function pushGoodsSoldNoReturnFooter(lines, contentWidth) {
  lines.push("");
  wrapLineToWidth(GOODS_SOLD_NO_RETURN_FOOTER, contentWidth).forEach((ln) =>
    lines.push(ln),
  );
}

function wrapLineToWidth(str, width) {
  const s = String(str ?? "")
    .replace(/\r/g, "")
    .trimEnd();
  if (width < 1) return [""];
  if (!s) return [""];
  const out = [];
  let rest = s;
  while (rest.length > 0) {
    if (rest.length <= width) {
      out.push(rest);
      break;
    }
    let breakAt = rest.lastIndexOf(" ", width);
    if (breakAt <= 0) breakAt = width;
    out.push(rest.slice(0, breakAt).trimEnd());
    rest = rest.slice(breakAt).trimStart();
  }
  return out.length ? out : [""];
}

function physicalBlocksFromRaw(rawLines, contentWidth) {
  const blocks = [];
  let current = [];
  const pushCurrent = () => {
    if (current.length > 0) {
      blocks.push(current);
      current = [];
    }
  };
  for (const raw of rawLines) {
    if (raw === TEXT_BLOCK_BREAK) {
      pushCurrent();
      continue;
    }
    const s = String(raw ?? "");
    const parts =
      s.length <= contentWidth ? [s] : wrapLineToWidth(s, contentWidth);
    for (const p of parts) current.push(p);
  }
  pushCurrent();
  return blocks;
}

function formatInsuranceTextPrint(
  rawLines,
  clinicDet,
  layout = TEXT_PRINT_LAYOUT,
) {
  const L = layout;
  const { columns, contentWidth } = L;
  const lineH = L.textLineHeightPt;
  const topBlankLines = Math.max(0, Math.ceil((L.topMarginPt ?? 0) / lineH));
  const bottomBlankLines = Math.max(0, Math.ceil(L.bottomMarginPt / lineH));
  const linesPerPage = Math.max(10, Math.floor(L.pageHeightPt / lineH));

  const blocks = physicalBlocksFromRaw(rawLines, contentWidth);

  const footerInner = [];
  if (L.footerEnabled && clinicDet?.name?.length) {
    footerInner.push(
      ...wrapLineToWidth(`-- ${clinicDet.name} --`, contentWidth),
    );
  }

  const pageTailLines =
    bottomBlankLines + footerInner.length + (L.pageNumberEnabled ? 1 : 0);
  const maxBodyPerPage = Math.max(
    5,
    linesPerPage - pageTailLines - topBlankLines,
  );

  const pages = [];
  let pageBody = [];
  let used = 0;
  const pushPage = () => {
    pages.push(pageBody);
    pageBody = [];
    used = 0;
  };
  for (const block of blocks) {
    if (!block?.length) continue;
    if (block.length > maxBodyPerPage) {
      if (used > 0) pushPage();
      let i = 0;
      while (i < block.length) {
        const slice = block.slice(i, i + maxBodyPerPage);
        pages.push(slice);
        i += maxBodyPerPage;
      }
      pageBody = [];
      used = 0;
      continue;
    }
    if (used + block.length > maxBodyPerPage && used > 0) {
      pushPage();
    }
    pageBody.push(...block);
    used += block.length;
  }
  if (pageBody.length > 0) pages.push(pageBody);
  if (pages.length === 0) pages.push([]);

  const out = [];
  const fullRow = (inner) => {
    const body = String(inner ?? "").slice(0, contentWidth);
    return (L.indent + body).padEnd(columns).slice(0, columns);
  };

  pages.forEach((pageBody, pi) => {
    for (let t = 0; t < topBlankLines; t++) {
      out.push(fullRow(""));
    }
    for (const line of pageBody) {
      out.push(fullRow(line));
    }
    for (let b = 0; b < bottomBlankLines; b++) {
      out.push(fullRow(""));
    }
    if (L.footerEnabled) {
      for (const fl of footerInner) {
        out.push(fullRow(fl));
      }
    }
    if (L.pageNumberEnabled) {
      const label =
        pages.length > 1 ? `Page ${pi + 1}/${pages.length}` : `Page ${pi + 1}`;
      const inner = label.slice(0, contentWidth);
      const pad = Math.max(0, contentWidth - inner.length);
      out.push(fullRow(`${" ".repeat(pad)}${inner}`));
    }
  });

  for (let e = 0; e < L.textModeExtraLines; e++) {
    out.push("");
  }

  return out.join("\n") + (out.length ? "\n" : "");
}

function dotTxtDivider(contentWidth = TEXT_PRINT_LAYOUT.contentWidth) {
  return "-".repeat(contentWidth);
}

function dotTxtCenterLine(text) {
  const t = String(text ?? "").trim();
  if (!t) return "";
  const w = TEXT_PRINT_LAYOUT.contentWidth;
  if (t.length >= w) return t.slice(0, w);
  const pad = Math.floor((w - t.length) / 2);
  return `${" ".repeat(pad)}${t}${" ".repeat(w - pad - t.length)}`;
}

function dotTxtDoubleLine(contentWidth = TEXT_PRINT_LAYOUT.contentWidth) {
  return "=".repeat(contentWidth);
}

function txtLineRight(label, value, contentWidth) {
  const chunk = `${label} ${value}`.trim();
  if (chunk.length >= contentWidth) return chunk.slice(0, contentWidth);
  return chunk.padStart(contentWidth);
}

function dotTxtPad(value, width, align = "left") {
  const raw = String(value ?? "");
  const trimmed = raw.length > width ? raw.slice(0, width) : raw;
  return align === "right" ? trimmed.padStart(width) : trimmed.padEnd(width);
}

function dotTxtTableLineLength(schema) {
  const gapChars = Math.max(0, schema.length - 1);
  return schema.reduce((acc, col) => acc + col.width, 0) + gapChars;
}

function getDotTxtTableSchema(_hasDiscCol, contentWidth) {
  const base = [
    { key: "sr", width: 4, align: "left", label: "Sr", minWidth: 2 },
    { key: "qty", width: 5, align: "left", label: "Qty", minWidth: 2 },
    { key: "pack", width: 10, align: "left", label: "UOM", minWidth: 4 },
    {
      key: "item",
      width: 24,
      align: "left",
      label: "Product Name",
      minWidth: 6,
    },
    { key: "sch", width: 12, align: "left", label: "Sch", minWidth: 3 },
    { key: "mfr", width: 8, align: "left", label: "Mfr", minWidth: 3 },
    { key: "batch", width: 10, align: "left", label: "Batch", minWidth: 3 },
    { key: "exp", width: 8, align: "left", label: "Expiry", minWidth: 4 },
    { key: "gst", width: 7, align: "right", label: "GST%", minWidth: 4 },
    { key: "amount", width: 10, align: "right", label: "Amount", minWidth: 6 },
  ];
  const schema = base.map((col) => ({ ...col }));
  const flexOrder = [
    "item",
    "pack",
    "batch",
    "exp",
    "sch",
    "mfr",
    "amount",
    "gst",
    "qty",
    "sr",
  ];

  const applyDelta = (delta) => {
    if (!delta) return 0;
    let remaining = delta;
    for (const key of flexOrder) {
      if (!remaining) break;
      const col = schema.find((c) => c.key === key);
      if (!col) continue;
      const minW = col.minWidth ?? 2;
      const next = Math.max(minW, col.width + remaining);
      const applied = next - col.width;
      col.width = next;
      remaining -= applied;
    }
    return remaining;
  };

  let delta = contentWidth - dotTxtTableLineLength(schema);
  if (delta > 0) {
    applyDelta(delta);
  } else if (delta < 0) {
    applyDelta(delta);
    // If still too wide, trim fixed-width columns down to their minimums.
    let overflow = dotTxtTableLineLength(schema) - contentWidth;
    if (overflow > 0) {
      for (const key of [...flexOrder].reverse()) {
        if (!overflow) break;
        const col = schema.find((c) => c.key === key);
        if (!col) continue;
        const minW = col.minWidth ?? 2;
        if (col.width <= minW) continue;
        const trim = Math.min(overflow, col.width - minW);
        col.width -= trim;
        overflow -= trim;
      }
    }
  }

  return schema.map(({ minWidth: _min, ...col }) => col);
}

function buildDotTxtTableLine(schema, getCell) {
  return schema
    .map((col) =>
      dotTxtPad(
        getCell(col),
        col.width,
        col.align === "right" ? "right" : "left",
      ),
    )
    .join(" ");
}

function pushDotTxtTableHeader(lines, contentWidth, hasDiscCol) {
  const schema = getDotTxtTableSchema(hasDiscCol, contentWidth);
  const header = buildDotTxtTableLine(schema, (col) => col.label);
  lines.push(
    header.length <= contentWidth ? header : header.slice(0, contentWidth),
  );
}

function pushDotTxtTableRow(lines, contentWidth, hasDiscCol, row) {
  const schema = getDotTxtTableSchema(hasDiscCol, contentWidth);
  const itemCol = schema.find((col) => col.key === "item");
  const itemWidth = itemCol?.width ?? 18;
  const itemParts = wrapLineToWidth(String(row?.item ?? ""), itemWidth);
  const rowCount = Math.max(1, itemParts.length);
  for (let idx = 0; idx < rowCount; idx++) {
    const line = buildDotTxtTableLine(schema, (col) => {
      if (col.key === "item") {
        return idx === 0 ? (itemParts[0] ?? "") : (itemParts[idx] ?? "");
      }
      if (idx > 0) return "";
      return row?.[col.key] ?? "";
    });
    lines.push(
      line.length <= contentWidth ? line : line.slice(0, contentWidth),
    );
  }
  lines.push(TEXT_BLOCK_BREAK);
}

function pushTwoColumnBlock(lines, leftRows, rightRows, contentWidth) {
  const gap = 2;
  const leftW = Math.min(34, Math.floor(contentWidth * 0.44));
  const rightW = Math.max(8, contentWidth - leftW - gap);
  const formatRightRow = (rowText) => {
    const raw = String(rowText ?? "").trim();
    if (!raw) return [""];
    if (raw.length <= rightW) return [raw];
    return wrapLineToWidth(raw, rightW);
  };
  const n = Math.max(leftRows.length, rightRows.length);
  for (let i = 0; i < n; i++) {
    const leftWrapped = wrapLineToWidth(String(leftRows[i] ?? ""), leftW);
    const rightWrapped = formatRightRow(rightRows[i] ?? "");
    const pairLines = Math.max(leftWrapped.length, rightWrapped.length);
    for (let j = 0; j < pairLines; j++) {
      const l = String(leftWrapped[j] ?? "")
        .slice(0, leftW)
        .padEnd(leftW);
      const r = String(rightWrapped[j] ?? "")
        .slice(0, rightW)
        .padStart(rightW);
      lines.push(`${l}${" ".repeat(gap)}${r}`);
    }
  }
}

function pushMetaThreeColumn(lines, left, mid, right, contentWidth) {
  const w1 = Math.floor(contentWidth * 0.3);
  const w2 = Math.floor(contentWidth * 0.32);
  const w3 = Math.max(8, contentWidth - w1 - w2 - 2);
  const a = String(left).slice(0, w1).padEnd(w1);
  const b = String(mid).slice(0, w2).padEnd(w2);
  const c = String(right).slice(0, w3).padStart(w3);
  lines.push(`${a} ${b} ${c}`.slice(0, contentWidth));
}

function normalizeWhitespace(value) {
  return String(value ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/[\n,]+/g, ", ")
    .replace(/\s+/g, " ")
    .replace(/,(\s*,)+/g, ", ")
    .trim();
}

function dedupeCommaAddressSegments(addrBody) {
  const bits = String(addrBody ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const out = [];
  for (const b of bits) {
    const low = b.toLowerCase();
    if (out.length && out[out.length - 1].toLowerCase() === low) continue;
    out.push(b);
  }
  return out.join(", ");
}

function pushBusinessRightAligned(lines, rows, contentWidth) {
  rows.forEach((row) => {
    const t = normalizeWhitespace(row ?? "");
    if (!t) {
      lines.push("");
      return;
    }
    const cleaned = /^business address:\s*/i.test(t)
      ? `Business Address: ${dedupeCommaAddressSegments(t.replace(/^business address:\s*/i, ""))}`
      : t;
    const wrapped = wrapLineToWidth(cleaned, contentWidth);
    wrapped.forEach((segment) => {
      const s =
        segment.length > contentWidth
          ? segment.slice(0, contentWidth)
          : segment;
      lines.push(s);
    });
  });
}

/** Name then address on the left (50%); phone + GSTIN on the right (50%). */
function pushReceiptHeaderTwoColumns(lines, leftRows, rightRows, contentWidth) {
  const rightItems = rightRows
    .map((row) => normalizeWhitespace(row ?? ""))
    .filter(Boolean);

  const leftColW = Math.floor(contentWidth / 2);
  const rightColW = contentWidth - leftColW;
  const leftWrapW = rightItems.length ? leftColW : contentWidth;

  const leftLines = [];
  for (const row of leftRows) {
    const t = String(row ?? "").trim();
    if (!t) continue;
    leftLines.push(...wrapLineToWidth(t, leftWrapW));
  }

  if (!rightItems.length) {
    leftLines.forEach((ln) => lines.push(ln.slice(0, contentWidth)));
    return;
  }

  const pushLeftLine = (text) => {
    const t = String(text ?? "").trim();
    if (t) lines.push(t.slice(0, leftColW));
  };

  const pushRightLine = (text) => {
    const t = String(text ?? "").trim();
    if (!t) return;
    const r = t.slice(0, rightColW).padStart(rightColW);
    lines.push(`${" ".repeat(leftColW)}${r}`.slice(0, contentWidth));
  };

  const pushPairLine = (leftText, rightText) => {
    const lt = String(leftText ?? "").trim();
    const rt = String(rightText ?? "").trim();
    if (lt && rt) {
      const l = lt.slice(0, leftColW).padEnd(leftColW);
      const r = rt.slice(0, rightColW).padStart(rightColW);
      lines.push(`${l}${r}`.slice(0, contentWidth));
      return;
    }
    if (lt) pushLeftLine(lt);
    if (rt) pushRightLine(rt);
  };

  const rightLines = [];
  for (const item of rightItems) {
    rightLines.push(...wrapLineToWidth(item, rightColW));
  }

  const pairRows = Math.max(leftLines.length, rightLines.length);
  for (let i = 0; i < pairRows; i++) {
    pushPairLine(leftLines[i] ?? "", rightLines[i] ?? "");
  }
}

function pushCompactKeyValueGrid(lines, leftPairs, rightPairs, contentWidth) {
  const leftColW = Math.floor(contentWidth / 2);
  const rightColW = contentWidth - leftColW;
  const maxRows = Math.max(leftPairs.length, rightPairs.length);

  const pushLeftLine = (text) => {
    const t = String(text ?? "").trim();
    if (t) lines.push(t.slice(0, leftColW));
  };

  const pushRightLine = (text) => {
    const t = String(text ?? "").trim();
    if (!t) return;
    const r = t.slice(0, rightColW).padStart(rightColW);
    lines.push(`${" ".repeat(leftColW)}${r}`.slice(0, contentWidth));
  };

  const pushPairLine = (leftText, rightText) => {
    const lt = String(leftText ?? "").trim();
    const rt = String(rightText ?? "").trim();
    if (lt && rt) {
      const l = lt.slice(0, leftColW).padEnd(leftColW);
      const r = rt.slice(0, rightColW).padStart(rightColW);
      lines.push(`${l}${r}`.slice(0, contentWidth));
      return;
    }
    if (lt) pushLeftLine(lt);
    if (rt) pushRightLine(rt);
  };

  const fmtPair = (pair, width) => {
    if (!pair) return [];
    const label = String(pair?.label ?? "").trim();
    const value = normalizeWhitespace(pair?.value ?? "");
    if (!label && !value) return [];
    const raw = label ? `${label}: ${value}` : value;
    return wrapLineToWidth(raw, width);
  };

  for (let i = 0; i < maxRows; i++) {
    const leftWrapped = fmtPair(leftPairs[i], leftColW);
    const rightWrapped = fmtPair(rightPairs[i], rightColW);
    if (!leftWrapped.length && !rightWrapped.length) continue;
    const rowLines = Math.max(leftWrapped.length, rightWrapped.length);
    for (let j = 0; j < rowLines; j++) {
      pushPairLine(leftWrapped[j] ?? "", rightWrapped[j] ?? "");
    }
  }
}

/** Pet / vet / invoice + date without truncating invoice no. */
function pushTreatmentMetaLines(
  lines,
  pet,
  vet,
  invoiceNo,
  dateStr,
  contentWidth,
) {
  const inv = String(invoiceNo ?? "").trim() || "—";
  const dt = String(dateStr ?? "").trim();
  const l1left = normalizeWhitespace(`Pet Name: ${pet}`);
  const l1right = normalizeWhitespace(`Vet Name: ${vet}`);
  const gap = 2;
  const half = Math.floor((contentWidth - gap) / 2);
  const a = l1left.slice(0, half).padEnd(half);
  const b = l1right
    .slice(0, contentWidth - half - gap)
    .padStart(contentWidth - half - gap);
  lines.push(`${a}${" ".repeat(gap)}${b}`.slice(0, contentWidth));

  const row2 = normalizeWhitespace(`Inv: ${inv}   Date: ${dt}`);
  if (row2.length <= contentWidth) {
    lines.push(row2.padStart(contentWidth));
  } else {
    lines.push(`Inv: ${inv}`.padStart(contentWidth));
    lines.push(`Date: ${dt}`.padStart(contentWidth));
  }
}

function pushTreatmentMoneyLines(lines, indent, contentWidth, parts) {
  const u = String(parts.unitStr ?? "");
  const tx = String(parts.taxStr ?? "");
  const tot = String(parts.totalStr ?? "");
  const money =
    parts.discStr !== undefined && parts.discStr !== null
      ? `Unit Rs ${u}  Disc ${parts.discStr}  Tax ${tx}  Total Rs ${tot}`
      : `Unit Rs ${u}  Tax ${tx}  Total Rs ${tot}`;
  const loc = String(parts.locStr ?? "");
  if (indent.length + loc.length + 1 + money.length <= contentWidth) {
    lines.push(`${indent}${loc}  ${money}`.slice(0, contentWidth));
    return;
  }
  if (indent.length + money.length <= contentWidth) {
    if (indent.length + loc.length <= contentWidth) {
      lines.push(`${indent}${loc}`.slice(0, contentWidth));
    } else {
      wrapLineToWidth(`${indent}${loc}`, contentWidth).forEach((w) =>
        lines.push(w),
      );
    }
    lines.push(`${indent}${money}`.slice(0, contentWidth));
    return;
  }
  wrapLineToWidth(`${indent}${loc}`, contentWidth).forEach((w) =>
    lines.push(w),
  );
  lines.push(`${indent}${money}`.slice(0, contentWidth));
}

function paymentStatusLabel(s) {
  if (s === "partiallypaid") return "Partially Paid";
  return String(s ?? "");
}

function safeDownloadBasename(name) {
  return String(name ?? "invoice")
    .replace(/[/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
}

function treatmentSubitemFilter(itm, selectedBill) {
  const isSubItem =
    (itm?.procedureId ?? "")?.toString()?.length > 0 &&
    !itm?.mainProcedure &&
    itm?.linked !== "N";
  if (isSubItem && !selectedBill?.showSublineItems) return false;
  return (
    itm?.type !== "consultation" &&
    itm?.type?.toLowerCase() !== "consumables" &&
    (selectedBill?.showSublineItems
      ? true
      : itm?.patientWorkbook
        ? itm?.mainProcedure
        : true)
  );
}

function getTreatmentLineAmounts(itm, selectedBill) {
  const showSubPrice = selectedBill?.showSublinePrice;
  const reqTotal = showSubPrice
    ? Number(itm?.total ?? 0)
    : itm?.list?.length > 0
      ? Number(
          Number(
            itm?.list
              ?.filter((li) =>
                selectedBill?.showSublinePrice
                  ? li?.type?.toLowerCase() !== "consumables"
                  : true,
              )
              ?.reduce((acc, li) => acc + Number(li?.total), 0) ?? 0,
          ) + Number(itm?.total ?? 0),
        )
      : Number(itm?.total ?? 0);
  const reqUnitPrice = showSubPrice
    ? Number(itm?.mrpPrice ?? itm?.price ?? 0)
    : itm?.list?.length > 0
      ? Number(
          Number(
            itm?.list
              ?.filter((li) =>
                selectedBill?.showSublinePrice
                  ? li?.type?.toLowerCase() !== "consumables"
                  : true,
              )
              ?.reduce(
                (acc, li) => acc + Number(li?.mrpPrice ?? li?.price),
                0,
              ) ?? 0,
          ) + Number(itm?.mrpPrice ?? itm?.price ?? 0),
        )
      : Number(itm?.mrpPrice ?? itm?.price ?? 0);
  const reqDiscount = showSubPrice
    ? Number(itm?.discount ?? 0)
    : itm?.list?.length > 0
      ? Number(
          Number(
            itm?.list
              ?.filter((li) =>
                selectedBill?.showSublinePrice
                  ? li?.type?.toLowerCase() !== "consumables"
                  : true,
              )
              ?.reduce((acc, li) => acc + Number(li?.discount), 0) ?? 0,
          ) + Number(itm?.discount ?? 0),
        )
      : Number(itm?.discount ?? 0);
  return { reqTotal, reqUnitPrice, reqDiscount };
}

const getNameWithValue = (itm) => {
  if (itm?.shortName?.length > 0) return itm?.shortName;
  if (itm?.tradeName?.length > 0) return itm?.tradeName;
  if (itm?.name?.length > 0) return itm?.name;
  return "";
};

function appendTreatmentTxt(
  lines,
  selectedBill,
  clinicDet,
  profile,
  clinicVets,
) {
  const treatmentList = getTreatmentRecordBasedType(
    selectedBill?.paymentDetails,
  );
  if (!treatmentList?.length) return;

  const cw = TEXT_PRINT_LAYOUT.contentWidth;
  const tt = getTaxAndSubTotalAndTotal(treatmentList);
  const rounded = roundWithSignedDiff(tt?.subTotal);

  const bizLeft = [];
  const bizName = clinicDet?.name ?? profile?.name ?? "";
  if (bizName) bizLeft.push(bizName);
  if (clinicDet?.address)
    bizLeft.push(dedupeCommaAddressSegments(clinicDet.address));
  const bizRight = [];
  if (clinicDet?.mobile?.length > 0)
    bizRight.push(`Phone: ${clinicDet.mobile}`);
  if (clinicDet?.taxId?.length > 0) bizRight.push(`GSTIN: ${clinicDet.taxId}`);
  pushReceiptHeaderTwoColumns(lines, bizLeft, bizRight, cw);
  lines.push(dotTxtDivider());
  const vetLine = `${getRightSaluation(clinicVets, selectedBill?.doctorId) ?? ""}${selectedBill?.doctorName ?? ""}`;
  pushCompactKeyValueGrid(
    lines,
    [
      { label: "Patient Name", value: selectedBill?.userName ?? "" },
      { label: "Phone Number", value: selectedBill?.mobile ?? "" },
      { label: "Pet Name", value: selectedBill?.petName ?? "" },
      {
        label: "Vet Name",
        value: vetLine?.trim()?.length ? `${vetLine}`.slice(0, cw) : "",
      },
    ],
    [
      {
        label: "Inv",
        value: selectedBill?.treatmentInvoiceNumber ?? "",
      },
      {
        label: "Date",
        value: moment(new Date(selectedBill?.createdDate)).format(
          "DD-MM-YYYY HH:mm",
        ),
      },
    ],
    cw,
  );
  lines.push(dotTxtDivider());
  const hasDiscCol = treatmentList.some((li) => li?.discount > 0);
  pushDotTxtTableHeader(lines, cw, hasDiscCol);
  lines.push(dotTxtDivider());
  let sr = 1;

  treatmentList
    ?.filter((pd) => pd?.type === "consultation")
    ?.forEach((con) => {
      const conName = con?.name === "Home" ? "Home Visit" : con?.name;
      const doc =
        selectedBill?.doctorName?.length > 0
          ? `${getRightSaluation(clinicVets, selectedBill?.doctorId) ?? ""}${selectedBill?.doctorName}`
          : "";
      const disc =
        hasDiscCol && con?.discount > 0
          ? Number(con.discount).toFixed(2)
          : hasDiscCol
            ? "-"
            : undefined;
      pushDotTxtTableRow(lines, cw, hasDiscCol, {
        sr: String(sr++),
        qty: "1.00",
        pack: "-",
        item: `Consultation (${conName})${doc ? ` - ${doc}` : ""}`,
        sch: "-",
        mfr: "-",
        batch: "-",
        exp: "-",
        gst:
          con?.tax > 0
            ? Number((Number(con.tax) * 100) / Number(con.total || 1)).toFixed(
                2,
              )
            : "-",
        amount: Number(con?.total ?? 0).toFixed(2),
      });
    });

  reorderList(treatmentList)
    ?.filter((pd) => treatmentSubitemFilter(pd, selectedBill))
    ?.forEach((itm) => {
      const { reqTotal, reqUnitPrice, reqDiscount } = getTreatmentLineAmounts(
        itm,
        selectedBill,
      );
      const isSubItem =
        (itm?.procedureId ?? "")?.toString()?.length > 0 &&
        !itm?.mainProcedure &&
        itm?.linked !== "N";
      const showSubPrice = selectedBill?.showSublinePrice;
      const skipPrice = isSubItem && !showSubPrice;
      let nm = String(getNameWithValue(itm));
      const hsn = itm?.hsnSacCode?.length > 0 ? itm.hsnSacCode : "-";
      const batch =
        itm?.stockId && !itm?.autoConsumed && itm?.batchNo?.length > 0
          ? itm.batchNo
          : "-";
      const exp =
        itm?.stockId && !itm?.autoConsumed && itm?.expiryDate?.length > 0
          ? moment(new Date(itm.expiryDate)).format("DD MMM YYYY")
          : "-";
      let qty = String(itm?.quantity ?? "");
      if (skipPrice) {
        pushDotTxtTableRow(lines, cw, hasDiscCol, {
          sr: String(sr++),
          qty,
          pack: getRightPackageUom(itm?.uomName, true)?.length
            ? getRightPackageUom(itm?.uomName, true)
            : "-",
          item: nm,
          sch: itm?.drugSchedule?.length ? itm.drugSchedule : "-",
          mfr: itm?.tradeName?.length ? itm.tradeName : "-",
          batch,
          exp,
          gst: "-",
          amount: "-",
        });
        return;
      }
      const unitN = Number(!showSubPrice ? reqTotal : reqUnitPrice).toFixed(2);
      const discTxt = hasDiscCol
        ? Number(reqDiscount) > 0
          ? Number(reqDiscount).toFixed(2)
          : "-"
        : undefined;
      pushDotTxtTableRow(lines, cw, hasDiscCol, {
        sr: String(sr++),
        qty,
        pack: itm?.uomName?.length ? itm.uomName : "-",
        item: nm,
        sch: itm?.drugSchedule?.length ? itm.drugSchedule : "-",
        mfr: itm?.tradeName?.length ? itm.tradeName : "-",
        batch,
        exp,
        gst:
          itm?.tax > 0 && Number(reqTotal) > 0
            ? Number((Number(itm.tax) * 100) / Number(reqTotal)).toFixed(2)
            : "-",
        amount: Number(reqTotal).toFixed(2),
      });
    });

  if (selectedBill?.showSublineItems) {
    const consumables = reorderList(treatmentList)?.filter(
      (pd) => pd?.type?.toLowerCase() === "consumables",
    );
    if (consumables?.length > 0 && selectedBill?.showSublinePrice) {
      const reqTotal =
        consumables?.reduce((acc, li) => acc + Number(li?.total), 0) ?? 0;
      const reqUnitPrice =
        consumables?.reduce(
          (acc, li) =>
            acc +
            Number(
              Number(li?.mrpPrice ?? li?.price ?? 0) *
                Number(li?.quantity ?? 1),
            ),
          0,
        ) ?? 0;
      const reqDiscount =
        consumables?.reduce((acc, li) => acc + Number(li?.discount), 0) ?? 0;
      const consTax = getTaxAndSubTotalAndTotal(consumables)?.tax ?? 0;
      pushDotTxtTableRow(lines, cw, hasDiscCol, {
        sr: String(sr++),
        qty: "1.00",
        pack: "-",
        item: "Medical Consumables (aggregate)",
        sch: "-",
        mfr: "-",
        batch: "-",
        exp: "-",
        gst:
          consTax > 0 && Number(reqTotal) > 0
            ? Number((Number(consTax) * 100) / Number(reqTotal)).toFixed(2)
            : "-",
        amount: Number(reqTotal).toFixed(2),
      });
    }
  }

  lines.push(TEXT_BLOCK_BREAK);
  lines.push(dotTxtDivider());
  const subLess =
    Number(tt?.subTotal ?? 0) -
    (Number(tt?.tax ?? 0) +
      Number(tt?.discount ?? 0) +
      Number(tt?.adminCharge ?? 0));
  const taxHalf = Number(tt?.tax ?? 0) / 2;
  const trRound = Number(roundWithSignedDiff(tt?.subTotal)?.difference).toFixed(
    2,
  );
  const trSummary = [
    `Subtotal: Rs ${Number(subLess).toFixed(2)}`,
    `Taxes (CGST): ${taxHalf > 0 ? `Rs ${taxHalf.toFixed(2)}` : "-"}`,
    `Taxes (SGST): ${taxHalf > 0 ? `Rs ${taxHalf.toFixed(2)}` : "-"}`,
    `Round Off: Rs ${trRound}`,
  ].join("  ");
  wrapLineToWidth(trSummary, cw).forEach((ln) => lines.push(ln));
  lines.push(dotTxtDoubleLine());
  lines.push(
    `Total: Rs ${Number(roundWithSignedDiff(tt?.subTotal)?.rounded).toFixed(2)}`,
  );
  lines.push(dotTxtDoubleLine());
  lines.push(TEXT_BLOCK_BREAK);
  if (selectedBill?.billedByName?.length) {
    lines.push("");
    lines.push(`Billed by: ${selectedBill.billedByName}   Pharmacist Sign`);
  }
  if (clinicDet?.disclaimer?.length > 0) {
    for (const w of wrapLineToWidth(
      `Disclaimer: ${clinicDet.disclaimer}`,
      cw,
    )) {
      lines.push(w);
    }
  }
  pushGoodsSoldNoReturnFooter(lines, cw);
  lines.push("");
}

function businessBlockPharmacy(selectedBill, clinicDet) {
  const name =
    (selectedBill?.type === "Pharmacy"
      ? (clinicDet?.pharmacyName ?? "")
      : selectedBill?.type === "Pet Shop"
        ? (clinicDet?.petShopName ?? "")
        : (clinicDet?.pharmacyName ?? "")) ||
    clinicDet?.name ||
    "";
  const addr =
    (selectedBill?.type === "Pharmacy"
      ? (clinicDet?.pharmacyAddress ?? "")
      : selectedBill?.type === "Pet Shop"
        ? (clinicDet?.petShopAddress ?? "")
        : (clinicDet?.pharmacyAddress ?? "")) ||
    clinicDet?.address ||
    "";
  const taxId =
    (selectedBill?.type === "Pet Shop"
      ? clinicDet?.petShopTaxId
      : clinicDet?.pharmacyTaxId) ??
    clinicDet?.taxId ??
    "";
  return { name, addr, taxId };
}

function appendPharmacyTxt(
  lines,
  selectedBill,
  clinicDet,
  profile,
  clinicVets,
) {
  const pharmacyList = getPharmacyRecordBasedType(selectedBill?.paymentDetails);
  if (!pharmacyList?.length) return;

  const cw = PHARMACY_TEXT_PRINT_LAYOUT.contentWidth;

  const { name, addr, taxId } = businessBlockPharmacy(selectedBill, clinicDet);
  const bizLeft = [];
  if (name) bizLeft.push(name);
  if (addr) bizLeft.push(dedupeCommaAddressSegments(addr));
  const bizRight = [];
  if (clinicDet?.mobile?.length > 0)
    bizRight.push(
      `Phone: ${clinicDet.mobile}${clinicDet?.id === 119630 ? `/9028045488` : ""}`,
    );
  if (taxId?.length > 0) bizRight.push(`GSTIN: ${taxId}`);
  bizRight.push(
    `DL NO: ${clinicDet?.id === 119630 ? "GA-NGO-7709,GA-NGO-7710,GA-NGO-7711" : ""}`,
  );
  pushReceiptHeaderTwoColumns(lines, bizLeft, bizRight, cw);
  lines.push(dotTxtDivider(cw));

  const pt = getTaxAndSubTotalAndTotal(pharmacyList);
  const pRounded = roundWithSignedDiff(pt?.subTotal);
  const vetLine = `${getRightSaluation(clinicVets, selectedBill?.doctorId) ?? ""}${selectedBill?.doctorName ?? ""}`;
  pushCompactKeyValueGrid(
    lines,
    [
      {
        label: "Doctor",
        value: vetLine?.trim()?.length ? `${vetLine}`.slice(0, cw) : "",
      },
      { label: "Client", value: selectedBill?.userName ?? "" },
      { label: "Pet Name", value: selectedBill?.petName ?? "" },
      { label: "Address", value: selectedBill?.address ?? "" },
    ],
    [
      {
        label: "Cash Bill No",
        value: selectedBill?.treatmentInvoiceNumber ?? "",
      },
      {
        label: "Date",
        value: moment(new Date(selectedBill?.createdDate)).format(
          "DD-MM-YYYY HH:mm",
        ),
      },
      {
        label: "Client GSTIN",
        value: "",
      },
    ],
    cw,
  );

  lines.push(dotTxtDivider(cw));
  const hasDisc = pharmacyList.some((li) => li?.discount > 0);
  const showPharmTax = clinicDet?.pharmacyTaxId?.length > 0;
  pushDotTxtTableHeader(lines, cw, hasDisc);
  lines.push(dotTxtDivider(cw));
  let sr = 1;

  pharmacyList
    ?.filter((pd) => pd?.type !== "consultation")
    ?.forEach((itm) => {
      const reqName = itm?.serviceId
        ? removeDuplicateBracketText(itm?.tradeName ?? itm?.name ?? "")
        : getNameWithValue(itm);
      const nm = String(reqName ?? "");
      if (nm?.length > cw) return nm?.substring(0, cw);
      const hsn = itm?.hsnSacCode?.length > 0 ? itm.hsnSacCode : "-";
      const batch =
        itm?.stockId && !itm?.autoConsumed && itm?.batchNo?.length > 0
          ? itm.batchNo
          : "-";
      const exp =
        itm?.stockId && !itm?.autoConsumed && itm?.expiryDate?.length > 0
          ? moment(new Date(itm.expiryDate)).format("MM-YY")
          : "-";
      let qty = String(itm?.quantity ?? "");
      const unit = Number(itm?.mrpPrice ?? itm?.price).toFixed(2);
      const discVal = Number(itm?.mrpDiscount ?? itm?.discount);
      const discTxt =
        hasDisc && discVal > 0 ? discVal.toFixed(2) : hasDisc ? "-" : "";
      pushDotTxtTableRow(lines, cw, hasDisc, {
        sr: String(sr++),
        qty,
        pack: getRightPackageUom(itm?.uomName, true)?.length
          ? getRightPackageUom(itm?.uomName, true)
          : "-",
        item: nm,
        sch: itm?.drugSchedule?.length ? itm.drugSchedule : "-",
        mfr: itm?.brand?.length ? (itm.brand ?? "")?.slice(0, 6) : "-",
        batch,
        exp,
        gst:
          showPharmTax && itm?.tax > 0 && Number(itm?.total ?? 0) > 0
            ? Number((Number(itm.tax) * 100) / Number(itm?.total ?? 1)).toFixed(
                2,
              )
            : "-",
        amount: Number(itm?.total ?? 0).toFixed(2),
      });
    });

  lines.push(TEXT_BLOCK_BREAK);
  lines.push(dotTxtDivider(cw));
  const phSub =
    Number(pt?.subTotal ?? 0) -
    (Number(pt?.tax ?? 0) +
      Number(pt?.discount ?? 0) +
      Number(pt?.adminCharge ?? 0));
  const phTaxHalf = Number(pt?.tax ?? 0) / 2;
  const phRound = Number(roundWithSignedDiff(pt?.subTotal)?.difference).toFixed(
    2,
  );
  const phSummaryParts = [];
  if (Number(pt?.discount) > 0) {
    phSummaryParts.push(`Discount: Rs ${Number(pt.discount).toFixed(2)}`);
  }
  phSummaryParts.push(
    `GST: ${pt?.tax > 0 ? `Rs ${pt?.tax?.toFixed(2)}` : "-"}`,
  );
  phSummaryParts.push(
    `CGST: ${phTaxHalf > 0 ? `Rs ${phTaxHalf.toFixed(2)}` : "-"}`,
  );
  phSummaryParts.push(
    `SGST: ${phTaxHalf > 0 ? `Rs ${phTaxHalf.toFixed(2)}` : "-"}`,
  );
  wrapLineToWidth(phSummaryParts.join("  "), cw).forEach((ln) =>
    lines.push(ln),
  );
  lines.push(dotTxtDoubleLine(cw));
  lines.push(
    `Total: Rs ${Number(roundWithSignedDiff(pt?.subTotal)?.rounded).toFixed(2)}`,
  );
  lines.push(dotTxtDoubleLine(cw));
  lines.push(TEXT_BLOCK_BREAK);
  if (selectedBill?.billedByName?.length) {
    lines.push("");
    lines.push(`Billed by: ${selectedBill.billedByName}   Pharmacist Sign`);
  }
  if (clinicDet?.disclaimer?.length > 0) {
    for (const w of wrapLineToWidth(
      `Disclaimer: ${clinicDet.disclaimer}`,
      cw,
    )) {
      lines.push(w);
    }
  }
  pushGoodsSoldNoReturnFooter(lines, cw);
  lines.push("");
}

function appendPetShopTxt(lines, selectedBill, clinicDet, profile, clinicVets) {
  const petList = getPetShopRecordBasedType(selectedBill?.paymentDetails);
  if (!petList?.length) return;

  const cw = TEXT_PRINT_LAYOUT.contentWidth;
  const bizLeft = [];
  bizLeft.push(
    `${clinicDet?.petShopName ?? profile?.petShopName ?? clinicDet?.name ?? ""}`,
  );
  const psAddr = clinicDet?.petShopAddress ?? clinicDet?.address ?? "";
  if (psAddr) bizLeft.push(dedupeCommaAddressSegments(psAddr));
  const psTax = clinicDet?.petShopTaxId ?? clinicDet?.taxId ?? "";
  const bizRight = [];
  if (clinicDet?.mobile?.length > 0)
    bizRight.push(`Phone: ${clinicDet.mobile}`);
  if (psTax?.length > 0) bizRight.push(`GSTIN: ${psTax}`);
  pushReceiptHeaderTwoColumns(lines, bizLeft, bizRight, cw);
  lines.push(dotTxtDivider());

  const pst = getTaxAndSubTotalAndTotal(petList);
  const psRounded = roundWithSignedDiff(pst?.subTotal);
  const vetLine = `${getRightSaluation(clinicVets, selectedBill?.doctorId) ?? ""}${selectedBill?.doctorName ?? ""}`;
  pushCompactKeyValueGrid(
    lines,
    [
      { label: "Patient Name", value: selectedBill?.userName ?? "" },
      { label: "Phone Number", value: selectedBill?.mobile ?? "" },
      { label: "Pet Name", value: selectedBill?.petName ?? "" },
      {
        label: "Vet Name",
        value: vetLine?.trim()?.length ? `${vetLine}`.slice(0, cw) : "",
      },
    ],
    [
      {
        label: "Inv",
        value: selectedBill?.treatmentInvoiceNumber ?? "",
      },
      {
        label: "Date",
        value: moment(new Date(selectedBill?.createdDate)).format(
          "DD-MM-YYYY HH:mm",
        ),
      },
      {
        label: "Payment Status",
        value: paymentStatusLabel(selectedBill?.status),
      },
    ],
    cw,
  );

  lines.push(dotTxtDivider());

  const hasDisc = petList.some((li) => li?.discount > 0);
  const showPetTax = clinicDet?.pharmacyTaxId?.length > 0;
  pushDotTxtTableHeader(lines, cw, hasDisc);
  lines.push(dotTxtDivider());
  let sr = 1;

  petList
    ?.filter((li) => li?.quantity > 0)
    ?.forEach((itm) => {
      let nm = String(getNameWithValue(itm));
      const hsn = itm?.hsnSacCode?.length > 0 ? itm.hsnSacCode : "-";
      const batch =
        itm?.stockId && !itm?.autoConsumed && itm?.batchNo?.length > 0
          ? itm.batchNo
          : "-";
      const exp =
        itm?.stockId && !itm?.autoConsumed && itm?.expiryDate?.length > 0
          ? moment(new Date(itm.expiryDate)).format("DD MMM YYYY")
          : "-";
      let qty = String(itm?.quantity ?? "");
      const unit = Number(itm?.mrpPrice ?? itm?.price).toFixed(2);
      const discVal = Number(itm?.mrpDiscount ?? itm?.discount);
      const discTxt =
        hasDisc && discVal > 0 ? discVal.toFixed(2) : hasDisc ? "-" : "";
      pushDotTxtTableRow(lines, cw, hasDisc, {
        sr: String(sr++),
        qty,
        pack: getRightPackageUom(itm?.uomName, true)?.length
          ? getRightPackageUom(itm?.uomName, true)
          : "-",
        item: nm,
        sch: itm?.drugSchedule?.length ? itm.drugSchedule : "-",
        mfr: itm?.tradeName?.length ? itm.tradeName : "-",
        batch,
        exp,
        gst:
          showPetTax && itm?.tax > 0 && Number(itm?.total ?? 0) > 0
            ? Number((Number(itm.tax) * 100) / Number(itm?.total ?? 1)).toFixed(
                2,
              )
            : "-",
        amount: Number(itm?.total ?? 0).toFixed(2),
      });
    });

  lines.push(TEXT_BLOCK_BREAK);
  lines.push(dotTxtDivider());
  const petSub =
    Number(pst?.subTotal ?? 0) -
    (Number(pst?.tax ?? 0) +
      Number(pst?.discount ?? 0) +
      Number(pst?.adminCharge ?? 0));
  const petTaxHalf = Number(pst?.tax ?? 0) / 2;
  const petRound = Number(
    roundWithSignedDiff(pst?.subTotal)?.difference,
  ).toFixed(2);
  const petSummaryParts = [];
  if (Number(pst?.discount) > 0) {
    petSummaryParts.push(`Discount: Rs ${Number(pst.discount).toFixed(2)}`);
  }
  petSummaryParts.push(`Subtotal: Rs ${Number(petSub).toFixed(2)}`);
  petSummaryParts.push(
    `Taxes (CGST): ${petTaxHalf > 0 ? `Rs ${petTaxHalf.toFixed(2)}` : "-"}`,
  );
  petSummaryParts.push(
    `Taxes (SGST): ${petTaxHalf > 0 ? `Rs ${petTaxHalf.toFixed(2)}` : "-"}`,
  );
  petSummaryParts.push(`Round Off: Rs ${petRound}`);
  wrapLineToWidth(petSummaryParts.join("  "), cw).forEach((ln) =>
    lines.push(ln),
  );
  lines.push(dotTxtDoubleLine());
  lines.push(
    `Total: Rs ${Number(roundWithSignedDiff(pst?.subTotal)?.rounded).toFixed(2)}`,
  );
  lines.push(dotTxtDoubleLine());
  lines.push(TEXT_BLOCK_BREAK);
  if (selectedBill?.billedByName?.length) {
    lines.push("");
    lines.push(`Billed by: ${selectedBill.billedByName}   Pharmacist Sign`);
  }
  if (clinicDet?.disclaimer?.length > 0) {
    for (const w of wrapLineToWidth(
      `Disclaimer: ${clinicDet.disclaimer}`,
      cw,
    )) {
      lines.push(w);
    }
  }
  pushGoodsSoldNoReturnFooter(lines, cw);
  lines.push("");
}

function buildDotMatrixInvoiceTxt({
  selectedBill,
  billsToShow = [],
  clinicDet,
  profile,
  clinicVets,
}) {
  if (!selectedBill) return "";
  const lines = [];
  if (
    billsToShow?.includes("treatment") &&
    getTreatmentRecordBasedType(selectedBill?.paymentDetails)?.length > 0 &&
    selectedBill?.type !== "Pharmacy" &&
    selectedBill?.type !== "Pet Shop"
  ) {
    appendTreatmentTxt(lines, selectedBill, clinicDet, profile, clinicVets);
  }
  if (
    billsToShow?.includes("pharmacy") &&
    getPharmacyRecordBasedType(selectedBill?.paymentDetails)?.length > 0
  ) {
    appendPharmacyTxt(lines, selectedBill, clinicDet, profile, clinicVets);
  }
  if (
    billsToShow?.includes("petshop") &&
    getPetShopRecordBasedType(selectedBill?.paymentDetails)?.length > 0
  ) {
    appendPetShopTxt(lines, selectedBill, clinicDet, profile, clinicVets);
  }
  if (!lines.join("\n").trim()) return "";
  const hasPharmacyOutput =
    billsToShow?.includes("pharmacy") &&
    getPharmacyRecordBasedType(selectedBill?.paymentDetails)?.length > 0;
  const hasTreatmentOutput =
    billsToShow?.includes("treatment") &&
    getTreatmentRecordBasedType(selectedBill?.paymentDetails)?.length > 0 &&
    selectedBill?.type !== "Pharmacy" &&
    selectedBill?.type !== "Pet Shop";
  const hasPetShopOutput =
    billsToShow?.includes("petshop") &&
    getPetShopRecordBasedType(selectedBill?.paymentDetails)?.length > 0;
  const usePharmacyTextLayout =
    hasPharmacyOutput && !hasTreatmentOutput && !hasPetShopOutput;
  return formatInsuranceTextPrint(
    lines,
    clinicDet,
    usePharmacyTextLayout ? PHARMACY_TEXT_PRINT_LAYOUT : TEXT_PRINT_LAYOUT,
  );
}

const InvoiceBillFooter = ({
  billedByName = "",
  tabList = [],
  subtotalLabel = "Subtotal",
  disclaimer,
  totalPrefix = "Rs ",
  discountPrefix = "Rs ",
  usePetShopTax = false,
  petShopTaxId,
}) => {
  const totals = getTaxAndSubTotalAndTotal(tabList);
  const roundedTotal = roundWithSignedDiff(totals?.subTotal)?.rounded;
  const roundOffDiff = roundWithSignedDiff(totals?.subTotal)?.difference;
  const taxHalf = Number(totals?.tax ?? 0) / 2;

  const formatTaxHalf = () => {
    if (usePetShopTax) {
      return petShopTaxId?.length > 0
        ? `${totalPrefix}${taxHalf.toFixed(2)}`
        : Number(0)?.toFixed(2);
    }
    return taxHalf > 0 ? `${totalPrefix}${taxHalf.toFixed(2)}` : "-";
  };

  return (
    <div className="billing-invoice-table-footer ph10">
      <hr className="thin-line2" />
      <div className="flex-row-between">
        <div className="w55Per ali-itm-flx-end">
          <div className="flex-column w100Per">
            <div className="flex-row-ali-cen mb5">
              <div className="txt-semi-bold fs12 black">Billed by:</div>
              <Typography className="font-bold fs12 gray4 ml10">
                {billedByName}
              </Typography>
            </div>
            {disclaimer?.length > 0 ? (
              <div className="flex-row-ali-cen mb5">
                <div className="txt-semi-bold fs12 black">Disclaimer:</div>
                <Typography className="fs12 txt-regular gray-color ml10">
                  {disclaimer}
                </Typography>
              </div>
            ) : null}
          </div>
        </div>
        <div className="w45Per flex-end pr10">
          <div className="flex-column w100Per">
            <div className="flex-row-ali-cen mv5">
              <div className="w60Per">
                <div className="fs14 txt-regular">{subtotalLabel}</div>
              </div>
              <div className="w40Per flex-end">
                <div className="fs14 txt-regular">
                  {totalPrefix}
                  {Number(Number(totals?.subTotalNew ?? 0))?.toFixed(2)}
                </div>
              </div>
            </div>
            {Number(totals?.discount) > 0 ? (
              <div className="flex-row-ali-cen mb5">
                <div className="w70Per">
                  <div className="fs14 txt-regular">Discount</div>
                </div>
                <div className="w30Per flex-end">
                  <div className="fs14 txt-regular">
                    {Number(totals?.discount) > 0
                      ? `${discountPrefix}${Number(totals?.discount).toFixed(2)}`
                      : "-"}
                  </div>
                </div>
              </div>
            ) : null}
            <div className="flex-row-ali-cen mv5">
              <div className="w60Per">
                <div className="fs14 txt-regular">Taxes (CGST)</div>
              </div>
              <div className="w40Per flex-end">
                <div className="fs14 txt-regular">{formatTaxHalf()}</div>
              </div>
            </div>
            <div className="flex-row-ali-cen mv5">
              <div className="w60Per">
                <div className="fs14 txt-regular">Taxes (SGST)</div>
              </div>
              <div className="w40Per flex-end">
                <div className="fs14 txt-regular">{formatTaxHalf()}</div>
              </div>
            </div>
            <div className="flex-row-ali-cen mv5">
              <div className="w60Per">
                <div className="fs14 txt-regular">Round Off</div>
              </div>
              <div className="w40Per flex-end">
                <div className="fs14 txt-regular">
                  {`${totalPrefix}${Number(Number(roundOffDiff))?.toFixed(2)}`}
                </div>
              </div>
            </div>
            <div className="balancecontainer ph5 mt10">
              <div className="flex-row-ali-cen w100Per">
                <div className="w60Per">
                  <div className="txt-semi-bold fs14 blue-color">Total</div>
                </div>
                <div className="w40Per flex-end">
                  <div className="txt-semi-bold fs14 blue-color">
                    {totalPrefix}
                    {Number(roundedTotal)?.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mb20" />
    </div>
  );
};

const BillingPreview = ({
  modVsble = false,
  selectedBill = null,
  onClosePreview = () => {},
  billsToShow = [],
}) => {
  const [isDotMatrixPrint, setDotMatrixPrint] = useState(false);
  const contentToPrint = useRef(null);
  const receiptToPrint = useRef(null);
  const { details: clinicDet } = useSelector((state) => state?.clinic);
  const clinicVets = useSelector((state) => state?.vet?.vets?.vets);
  const user = localStorage.getItem("user");
  const profile = JSON.parse(user);
  const isBillCombined =
    (clinicDet?.billingMode ?? "")?.toLowerCase() === "combined";
  const isBillEstimate =
    (selectedBill?.type === "Clinic" && !selectedBill?.appointmentId) ||
    (selectedBill?.type === "Pharmacy" && selectedBill?.checkOut !== "Y") ||
    (selectedBill?.type === "Pet Shop" && selectedBill?.checkOut !== "Y");
  const treatTabList = getTreatmentRecordBasedType(
    selectedBill?.paymentDetails,
    !selectedBill?.estimate,
  );
  const pharTabList = getPharmacyRecordBasedType(
    selectedBill?.paymentDetails,
    !selectedBill?.estimate,
  );
  const petShopTabList = getPetShopRecordBasedType(
    selectedBill?.paymentDetails,
    !selectedBill?.estimate,
  );
  const resolvedBillsToShow = useMemo(
    () => resolveBillsToShow(billsToShow, selectedBill),
    [billsToShow, selectedBill],
  );
  const isPharmacyOnlyDotMatrixTxt =
    isDotMatrixPrint &&
    resolvedBillsToShow?.includes("pharmacy") &&
    pharTabList?.length > 0 &&
    !(
      (resolvedBillsToShow?.includes("treatment") &&
        treatTabList?.length > 0) ||
      (resolvedBillsToShow?.includes("petshop") && petShopTabList?.length > 0)
    );
  const hasPharmacyBill =
    (selectedBill?.type?.toLowerCase() === "pharmacy" ||
      (selectedBill?.estimate &&
        selectedBill?.type?.toLowerCase() === "clinic")) &&
    resolvedBillsToShow?.includes("pharmacy") &&
    pharTabList?.length > 0;
  const hasPetShopBill =
    (selectedBill?.type?.toLowerCase() === "pet shop" ||
      (selectedBill?.estimate &&
        selectedBill?.type?.toLowerCase() === "clinic")) &&
    resolvedBillsToShow?.includes("petshop") &&
    petShopTabList?.length > 0;
  const showTreatmentBill =
    (selectedBill?.type?.toLowerCase() === "clinic" && isBillCombined) ||
    (resolvedBillsToShow?.includes("treatment") && treatTabList?.length > 0);

  useEffect(() => {
    if (!modVsble || isDotMatrixPrint || typeof window === "undefined") return;

    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      const invoiceNode = contentToPrint.current;
      const receiptNode = receiptToPrint.current;
      if (invoiceNode) warmBillingPrintFrame(invoiceNode);
      else if (receiptNode) warmBillingPrintFrame(receiptNode);
    };

    const frameId = requestAnimationFrame(warm);

    const invoiceNode = contentToPrint.current;
    const receiptNode = receiptToPrint.current;
    const imgs = [
      ...(invoiceNode ? [...invoiceNode.querySelectorAll("img")] : []),
      ...(receiptNode ? [...receiptNode.querySelectorAll("img")] : []),
    ];
    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", warm, { once: true });
        img.addEventListener("error", warm, { once: true });
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      imgs.forEach((img) => {
        img.removeEventListener("load", warm);
        img.removeEventListener("error", warm);
      });
    };
  }, [modVsble, isDotMatrixPrint, selectedBill, billsToShow, clinicDet?.image]);

  const dotMatrixPageStyle = `
    @page {
      /*
       * Tractor-feed stock (full sheet): ${DOT_MATRIX_STANDARD.fullWidthIn}in x ${DOT_MATRIX_STANDARD.pageHeightIn}in
       * (~24.1 cm x 27.9 cm) including perforated sides.
       * Printable width after tearing side strips: ${DOT_MATRIX_STANDARD.printableWidthIn}in (~20.3 cm).
       * Side strip + sprocket zone: standard range ~${DOT_MATRIX_STANDARD.sideStripRangeIn}in each side;
       * this layout uses ${DOT_MATRIX_STANDARD.sideStripIn}in each side.
       * Hole spacing: standard tractor-feed pitch ~${DOT_MATRIX_STANDARD.holePitchIn}in (printer hardware).
       */
      size: ${DOT_MATRIX_STANDARD.fullWidthIn}in ${DOT_MATRIX_STANDARD.pageHeightIn}in;
      margin: 0;
    }

    @media print {
      body {
        margin: 0 !important;
      }

      .billing-print-root.billing-dotmatrix,
      .billing-print-root.billing-dotmatrix *,
      .billing-print-root.billing-dotmatrix *::before,
      .billing-print-root.billing-dotmatrix *::after {
        box-sizing: border-box !important;
      }

      .billing-print-root.billing-dotmatrix {
        width: ${DOT_MATRIX_STANDARD.fullWidthIn}in !important;
        max-width: ${DOT_MATRIX_STANDARD.fullWidthIn}in !important;
        min-width: ${DOT_MATRIX_STANDARD.fullWidthIn}in !important;
        padding-left: ${DOT_MATRIX_STANDARD.sideStripIn}in !important;
        padding-right: ${DOT_MATRIX_STANDARD.sideStripIn}in !important;
        margin: 0 !important;
        box-sizing: border-box !important;
        /* Content box width = 8in (tear-off printable area) */
        min-height: ${DOT_MATRIX_STANDARD.pageHeightIn}in !important;
        height: ${DOT_MATRIX_STANDARD.pageHeightIn}in !important;
        max-height: ${DOT_MATRIX_STANDARD.pageHeightIn}in !important;
        font-family: "Courier New", monospace !important;
        font-size: ${DOT_MATRIX_STANDARD.fontSizePx}px !important;
        line-height: 1.1 !important;
        color: #141414 !important;
        background: #f9f7f3 !important;
        page-break-after: auto !important;
        break-after: auto !important;
        overflow: hidden !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      .billing-print-root.billing-dotmatrix > * {
        width: 100% !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
      }

      .billing-print-root.billing-dotmatrix .billing-dotmatrix-txt {
        margin: 0 !important;
        padding: 0 !important;
        white-space: pre !important;
        word-wrap: normal !important;
        overflow-wrap: normal !important;
        font-family: "Courier New", Courier, monospace !important;
        font-size: ${DOT_MATRIX_STANDARD.fontSizePx}px !important;
        line-height: 1.1 !important;
        color: #141414 !important;
        background: transparent !important;
        border: none !important;
        width: 100% !important;
        overflow: visible !important;
      }

      .billing-print-root.billing-dotmatrix .billing-dotmatrix-txt.billing-dotmatrix-pharmacy {
        padding: ${PHARMACY_DOT_MATRIX_MARGINS.verticalMm}mm ${PHARMACY_DOT_MATRIX_MARGINS.horizontalMm}mm !important;
        font-family: ${PHARMACY_DOT_MATRIX_MARGINS.fontFamily} !important;
        box-sizing: border-box !important;
      }

      /* Align right-side summary/header blocks cleanly in print */
      .billing-print-root.billing-dotmatrix .w30Per.flex1-end,
      .billing-print-root.billing-dotmatrix .flex1-end.w40Per {
        justify-content: flex-end !important;
      }

      .billing-print-root.billing-dotmatrix .custom-table {
        width: 100% !important;
        table-layout: fixed !important;
        border-collapse: collapse !important;
        margin-left: 0 !important;
      }

      .billing-print-root.billing-dotmatrix .line,
      .billing-print-root.billing-dotmatrix .thin-line {
        width: 100% !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
      }

      .billing-print-root.billing-dotmatrix .box-container {
        width: 100% !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
        margin-bottom: 12px !important;
        border: 1px solid #ccc !important;
      }

      .billing-print-root.billing-dotmatrix .custom-table th {
        white-space: normal !important;
        word-break: break-word !important;
        overflow-wrap: anywhere !important;
        line-height: 1.1 !important;
      }

      .billing-print-root.billing-dotmatrix .custom-table th,
      .billing-print-root.billing-dotmatrix .custom-table td {
        font-size: ${DOT_MATRIX_STANDARD.fontSizePx}px !important;
        padding: 3px 2px !important;
        vertical-align: top !important;
        word-break: normal !important;
        overflow-wrap: normal !important;
      }

      .billing-print-root.billing-dotmatrix .custom-table td {
        white-space: nowrap !important;
      }

      /* Keep item/service name readable without breaking numeric columns */
      .billing-print-root.billing-dotmatrix .custom-table th:nth-child(2),
      .billing-print-root.billing-dotmatrix .custom-table td:nth-child(2) {
        white-space: normal !important;
        word-break: break-word !important;
        overflow-wrap: anywhere !important;
      }

      /* Dot-matrix column widths (EXPIRY + TOTAL wider for dates / Rs amounts) */
      .billing-print-root.billing-dotmatrix .custom-table th:nth-child(1),
      .billing-print-root.billing-dotmatrix .custom-table td:nth-child(1) { width: 9% !important; }
      .billing-print-root.billing-dotmatrix .custom-table th:nth-child(2),
      .billing-print-root.billing-dotmatrix .custom-table td:nth-child(2) { width: 23% !important; }
      .billing-print-root.billing-dotmatrix .custom-table th:nth-child(3),
      .billing-print-root.billing-dotmatrix .custom-table td:nth-child(3) { width: 9% !important; }
      .billing-print-root.billing-dotmatrix .custom-table th:nth-child(4),
      .billing-print-root.billing-dotmatrix .custom-table td:nth-child(4) {
        width: 13% !important;
        min-width: 5.5rem !important;
      }
      .billing-print-root.billing-dotmatrix .custom-table th:nth-child(5),
      .billing-print-root.billing-dotmatrix .custom-table td:nth-child(5) { width: 5% !important; }
      .billing-print-root.billing-dotmatrix .custom-table th:nth-child(6),
      .billing-print-root.billing-dotmatrix .custom-table td:nth-child(6) { width: 11% !important; }
      .billing-print-root.billing-dotmatrix .custom-table th:nth-child(7),
      .billing-print-root.billing-dotmatrix .custom-table td:nth-child(7) { width: 9% !important; }
      .billing-print-root.billing-dotmatrix .custom-table th:nth-child(8):not(:last-child),
      .billing-print-root.billing-dotmatrix .custom-table td:nth-child(8):not(:last-child) { width: 9% !important; }
      .billing-print-root.billing-dotmatrix .custom-table th:last-child,
      .billing-print-root.billing-dotmatrix .custom-table td:last-child {
        width: 15% !important;
        min-width: 6.25rem !important;
      }

      .billing-print-root.billing-dotmatrix table,
      .billing-print-root.billing-dotmatrix thead,
      .billing-print-root.billing-dotmatrix tbody,
      .billing-print-root.billing-dotmatrix tr {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      tr,
      .invoice-row {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .footer-section {
        page-break-before: auto !important;
        break-before: auto !important;
      }

      /* Prevent invalid spacer blocks inside table body from creating overflow pages */
      .billing-print-root.billing-dotmatrix .custom-table tbody > div {
        display: none !important;
      }
      .billing-print-root.billing-dotmatrix .custom-table .mb20 {
        display: none !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      /* Trim trailing spacing that can force an extra blank page */
      .billing-print-root.billing-dotmatrix .mb20:last-child {
        margin-bottom: 0 !important;
      }
      .billing-print-root.billing-dotmatrix .mb20,
      .billing-print-root.billing-dotmatrix .mv20,
      .billing-print-root.billing-dotmatrix .mt30 {
        break-after: avoid-page !important;
      }

      /* Reduce top spacing on print */
      .billing-print-root.billing-dotmatrix .mt30 {
        margin-top: 8px !important;
      }
      .billing-print-root.billing-dotmatrix .mv20 {
        margin-top: 8px !important;
        margin-bottom: 8px !important;
      }

      /* Tax invoice / classic dot-matrix reference layout */
      .billing-print-root.billing-dotmatrix .line {
        background: transparent !important;
        background-color: transparent !important;
        height: 0 !important;
        border: none !important;
        border-top: 1px dashed #222 !important;
        margin: 8px 0 !important;
        width: 100% !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
      .billing-print-root.billing-dotmatrix .thin-line,
      .billing-print-root.billing-dotmatrix .thin-line2 {
        background: transparent !important;
        height: 0 !important;
        border: none !important;
        border-top: 1px dashed #555 !important;
        margin: 6px 0 !important;
        width: 100% !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
      .billing-print-root.billing-dotmatrix .box-container {
        border-radius: 0 !important;
        border: 1px solid #2a2a2a !important;
        background: #fff !important;
      }
      .billing-print-root.billing-dotmatrix .custom-table th,
      .billing-print-root.billing-dotmatrix .custom-table td {
        border: 1px solid #333 !important;
      }
      .billing-print-root.billing-dotmatrix .custom-table thead tr {
        background: #e6e6e6 !important;
      }
      .billing-print-root.billing-dotmatrix .flex-row-ali-cen.mv20 .w40Per.flex-center .fs18.blue-color {
        color: #000 !important;
        font-weight: 700 !important;
        letter-spacing: 0.1em !important;
        text-transform: uppercase !important;
        font-size: ${DOT_MATRIX_STANDARD.fontSizePx}px !important;
      }
      .billing-print-root.billing-dotmatrix .w30Per.flex1-end .flex-column {
        align-items: flex-end !important;
        text-align: right !important;
      }
      .billing-print-root.billing-dotmatrix .w30Per.flex1-end .font-bold.fs12.blue-color,
      .billing-print-root.billing-dotmatrix .w30Per.flex1-end .font-medium.blue-color.fs12,
      .billing-print-root.billing-dotmatrix .header-right-text2 {
        color: #1a1a1a !important;
      }
      .billing-print-root.billing-dotmatrix .balancecontainer {
        border-top: 3px double #000 !important;
        border-bottom: 3px double #000 !important;
        padding-top: 8px !important;
        padding-bottom: 8px !important;
        margin-top: 8px !important;
      }
      .billing-print-root.billing-dotmatrix .balancecontainer .blue-color {
        color: #000 !important;
      }
      .billing-print-root.billing-dotmatrix .txt-semi-bold.fs12.black,
      .billing-print-root.billing-dotmatrix .txtstyle {
        font-weight: 600 !important;
      }
    }
  `;

  // On-screen simulation: standard 9.5in x 11in sheet, 8in printable column, 0.75in side strips.
  const dotMatrixPreviewStyle = `
    .billing-print-root.billing-dotmatrix {
      width: ${DOT_MATRIX_STANDARD.fullWidthIn}in !important;
      max-width: ${DOT_MATRIX_STANDARD.fullWidthIn}in !important;
      min-width: ${DOT_MATRIX_STANDARD.fullWidthIn}in !important;
      margin: 0 auto !important;
      padding-left: ${DOT_MATRIX_STANDARD.sideStripIn}in !important;
      padding-right: ${DOT_MATRIX_STANDARD.sideStripIn}in !important;
      box-sizing: border-box !important;
      min-height: ${DOT_MATRIX_STANDARD.pageHeightIn}in !important;
      font-family: "Courier New", monospace !important;
      font-size: ${DOT_MATRIX_STANDARD.fontSizePx}px !important;
      line-height: 1.15 !important;
      color: #141414 !important;
      /* Center = 8in printable; left/right = 0.75in tear-off strips (within standard 0.5-0.75in range). */
      background-color: #faf8f4 !important;
      background-image: linear-gradient(90deg, #d4cfc7 0%, #e6e1d8 100%),
        linear-gradient(270deg, #d4cfc7 0%, #e6e1d8 100%) !important;
      background-size: ${DOT_MATRIX_STANDARD.sideStripIn}in 100%, ${DOT_MATRIX_STANDARD.sideStripIn}in 100% !important;
      background-position: left top, right top !important;
      background-repeat: no-repeat, no-repeat !important;
    }

    /* No scale here: html2pdf uses html2canvas on this node; scale() inflates
       bitmap height and causes an extra PDF page for the same 11in sheet. */
    @media screen {
      .billing-print-root.billing-dotmatrix {
        transform: none !important;
      }
    }

    .billing-print-root.billing-dotmatrix,
    .billing-print-root.billing-dotmatrix *,
    .billing-print-root.billing-dotmatrix *::before,
    .billing-print-root.billing-dotmatrix *::after {
      box-sizing: border-box !important;
    }

    .billing-print-root.billing-dotmatrix .w30Per.flex1-end,
    .billing-print-root.billing-dotmatrix .flex1-end.w40Per {
      justify-content: flex-end !important;
    }

    .billing-print-root.billing-dotmatrix .custom-table {
      width: 100% !important;
      table-layout: fixed !important;
      border-collapse: collapse !important;
      margin-left: 0 !important;
    }

    .billing-print-root.billing-dotmatrix .line {
      background: transparent !important;
      height: 0 !important;
      border: none !important;
      border-top: 1px dashed #222 !important;
      margin: 8px 0 !important;
      width: 100% !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
    .billing-print-root.billing-dotmatrix .thin-line,
    .billing-print-root.billing-dotmatrix .thin-line2 {
      background: transparent !important;
      height: 0 !important;
      border: none !important;
      border-top: 1px dashed #555 !important;
      margin: 6px 0 !important;
      width: 100% !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .billing-print-root.billing-dotmatrix .box-container {
      width: 100% !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      margin-bottom: 12px !important;
      border-radius: 0 !important;
      border: 1px solid #2a2a2a !important;
      background: #fff !important;
    }

    .billing-print-root.billing-dotmatrix .custom-table th,
    .billing-print-root.billing-dotmatrix .custom-table td {
      border: 1px solid #333 !important;
    }
    .billing-print-root.billing-dotmatrix .custom-table thead tr {
      background: #e6e6e6 !important;
    }

    .billing-print-root.billing-dotmatrix .flex-row-ali-cen.mv20 .w40Per.flex-center .fs18.blue-color {
      color: #000 !important;
      font-weight: 700 !important;
      letter-spacing: 0.1em !important;
      text-transform: uppercase !important;
      font-size: ${DOT_MATRIX_STANDARD.fontSizePx}px !important;
    }
    .billing-print-root.billing-dotmatrix .w30Per.flex1-end .flex-column {
      align-items: flex-end !important;
      text-align: right !important;
    }
    .billing-print-root.billing-dotmatrix .w30Per.flex1-end .font-bold.fs12.blue-color,
    .billing-print-root.billing-dotmatrix .w30Per.flex1-end .font-medium.blue-color.fs12,
    .billing-print-root.billing-dotmatrix .header-right-text2 {
      color: #1a1a1a !important;
    }
    .billing-print-root.billing-dotmatrix .balancecontainer {
      border-top: 3px double #000 !important;
      border-bottom: 3px double #000 !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
      margin-top: 8px !important;
    }
    .billing-print-root.billing-dotmatrix .balancecontainer .blue-color {
      color: #000 !important;
    }

    .billing-print-root.billing-dotmatrix .custom-table th {
      white-space: normal !important;
      word-break: break-word !important;
      overflow-wrap: anywhere !important;
      line-height: 1.1 !important;
    }

    .billing-print-root.billing-dotmatrix .custom-table th,
    .billing-print-root.billing-dotmatrix .custom-table td {
      font-size: ${DOT_MATRIX_STANDARD.fontSizePx}px !important;
      padding: 3px 2px !important;
      vertical-align: top !important;
      word-break: normal !important;
      overflow-wrap: normal !important;
    }

    .billing-print-root.billing-dotmatrix .custom-table td {
      white-space: nowrap !important;
    }

    .billing-print-root.billing-dotmatrix .custom-table th:nth-child(2),
    .billing-print-root.billing-dotmatrix .custom-table td:nth-child(2) {
      white-space: normal !important;
      word-break: break-word !important;
      overflow-wrap: anywhere !important;
    }

    /* Dot-matrix column widths (EXPIRY + TOTAL wider for dates / Rs amounts) */
    .billing-print-root.billing-dotmatrix .custom-table th:nth-child(1),
    .billing-print-root.billing-dotmatrix .custom-table td:nth-child(1) { width: 9% !important; }
    .billing-print-root.billing-dotmatrix .custom-table th:nth-child(2),
    .billing-print-root.billing-dotmatrix .custom-table td:nth-child(2) { width: 23% !important; }
    .billing-print-root.billing-dotmatrix .custom-table th:nth-child(3),
    .billing-print-root.billing-dotmatrix .custom-table td:nth-child(3) { width: 9% !important; }
    .billing-print-root.billing-dotmatrix .custom-table th:nth-child(4),
    .billing-print-root.billing-dotmatrix .custom-table td:nth-child(4) {
      width: 13% !important;
      min-width: 5.5rem !important;
    }
    .billing-print-root.billing-dotmatrix .custom-table th:nth-child(5),
    .billing-print-root.billing-dotmatrix .custom-table td:nth-child(5) { width: 5% !important; }
    .billing-print-root.billing-dotmatrix .custom-table th:nth-child(6),
    .billing-print-root.billing-dotmatrix .custom-table td:nth-child(6) { width: 11% !important; }
    .billing-print-root.billing-dotmatrix .custom-table th:nth-child(7),
    .billing-print-root.billing-dotmatrix .custom-table td:nth-child(7) { width: 9% !important; }
    .billing-print-root.billing-dotmatrix .custom-table th:nth-child(8):not(:last-child),
    .billing-print-root.billing-dotmatrix .custom-table td:nth-child(8):not(:last-child) { width: 9% !important; }
    .billing-print-root.billing-dotmatrix .custom-table th:last-child,
    .billing-print-root.billing-dotmatrix .custom-table td:last-child {
      width: 15% !important;
      min-width: 6.25rem !important;
    }

    .billing-print-root.billing-dotmatrix .billing-dotmatrix-txt {
      margin: 0 !important;
      padding: 0 !important;
      white-space: pre !important;
      word-wrap: normal !important;
      overflow-wrap: normal !important;
      font-family: "Courier New", Courier, monospace !important;
      font-size: ${DOT_MATRIX_STANDARD.fontSizePx}px !important;
      line-height: 1.15 !important;
      color: #141414 !important;
      background: transparent !important;
      border: none !important;
      width: 100% !important;
      max-width: 100% !important;
      overflow-x: auto !important;
    }

    .billing-print-root.billing-dotmatrix .billing-dotmatrix-txt.billing-dotmatrix-pharmacy {
      padding: ${PHARMACY_DOT_MATRIX_MARGINS.verticalMm}mm ${PHARMACY_DOT_MATRIX_MARGINS.horizontalMm}mm !important;
      font-family: ${PHARMACY_DOT_MATRIX_MARGINS.fontFamily} !important;
      box-sizing: border-box !important;
    }
  `;

  async function printInvoiceFunc(contentText) {
    try {
      const response = await printApi.post("/print", {
        type: "text", // Tells the server to use buildTextDocument
        condensed: true, // Ensures 13.5 CPI so text doesn't overflow
        continuousPaper: true, // Tells agent to use trailing lines instead of page clears
        formFeedLines: 3, // Feeds perfectly past the tearing line
        content: contentText, // The main body text
      });
    } catch (err) {
      console.error("Dot-matrix print failed:", err);
      window.alert(
        "Could not reach the local print service. Ensure the VetInstant print app is running on port 1818, then try again.",
      );
    }
  }

  const onDownloadInvoice = () => {
    const txt = buildDotMatrixInvoiceTxt({
      selectedBill,
      billsToShow: resolvedBillsToShow,
      clinicDet,
      profile,
      clinicVets,
    });
    if (!txt?.trim()) return;

    printInvoiceFunc(txt);
    return;
  };

  const onPrintInvoice = () => {
    if (isDotMatrixPrint) {
      onDownloadInvoice();
      return;
    }
    printBillingInvoice(
      contentToPrint.current,
      `${clinicDet?.name ?? ""}_invoice`,
    );
  };

  const handlePrintInvoice = () => {
    printBillingInvoice(
      receiptToPrint.current,
      `${clinicDet?.name ?? ""}_receipt`,
    );
  };

  const getPaySumTableHead = () => {
    return (
      <thead>
        <tr>
          <th className="w10Per">Receipt Date</th>
          <th className="w20Per">Receipt No.</th>
          <th className="w20Per">Amount Payable</th>
          <th className="w10Per">Payment Mode</th>
          <th className="w20Per">Amount Paid</th>
          <th className="w20Per">Balance Due</th>
        </tr>
      </thead>
    );
  };

  const getTabBasedCollectionsWithBalance = (tab = "treatment") => {
    const totalAmount = selectedBill?.totalAmt ?? 0;
    let runningBalance = totalAmount;
    return getTabBasedCollections(tab)?.map((item) => {
      const balanceBefore = runningBalance;

      runningBalance -= item?.amountPaid ?? 0;

      return {
        ...item,
        balanceBefore,
        balanceAfter: runningBalance,
      };
    });
  };

  const getTabBasedCollections = (tab = "treatment") => {
    const reqSource =
      tab === "treatment"
        ? ["clinic", "treatment"]
        : tab === "pharmacy"
          ? ["pharmacy"]
          : ["petshop"];
    const reqList = [...(selectedBill?.paymentCollections ?? [])]
      ?.filter((li) => !(li?.receiptNo ?? "").includes("CN"))
      // ?.filter((li) => reqSource.includes((li?.source ?? "")?.toLowerCase()))
      ?.sort((a, b) => {
        const ad = Number(a?.collectionId);
        const bd = Number(b?.collectionId);
        return (Number.isFinite(bd) ? bd : 0) - (Number.isFinite(ad) ? ad : 0);
      });
    return reqList ?? [];
  };

  return (
    <CustomModal
      open={modVsble}
      onClose={onClosePreview}
      header=""
      modal
      modalWidth={90}
    >
      <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
        {isDotMatrixPrint ? <style>{dotMatrixPreviewStyle}</style> : null}
        <div
          ref={contentToPrint}
          className={`billing-print-root${
            isDotMatrixPrint ? " billing-dotmatrix" : ""
          }`}
        >
          {isDotMatrixPrint ? (
            <pre
              className={`billing-dotmatrix-txt${
                isPharmacyOnlyDotMatrixTxt ? " billing-dotmatrix-pharmacy" : ""
              }`}
            >
              {buildDotMatrixInvoiceTxt({
                selectedBill,
                billsToShow: resolvedBillsToShow,
                clinicDet,
                profile,
                clinicVets,
              })}
            </pre>
          ) : (
            <>
              {showTreatmentBill ? (
                <>
                  {selectedBill?.type !== "Pharmacy" &&
                  selectedBill?.type !== "Pet Shop" ? (
                    <div className="mt30">
                      <div className="flex-row-ali-cen mv20">
                        <div className="w30Per">
                          {isDotMatrixPrint ? null : (
                            <div className="pl20">
                              <div>
                                {clinicDet?.image ? (
                                  <img
                                    src={clinicDet?.image}
                                    className="img-h75"
                                    alt=""
                                  />
                                ) : (
                                  <div className="flex-center h50img">
                                    <Typography className="font-bold fs30 white-color capitalize">
                                      {clinicDet?.name?.[0]}
                                    </Typography>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="w40Per flex-center">
                          <div className="font-bold fs18 blue-color">
                            {isBillEstimate
                              ? "BILL ESTIMATE"
                              : selectedBill?.checkOut !== "Y"
                                ? "PROFORMA INVOICE"
                                : isBillCombined
                                  ? "TAX INVOICE"
                                  : "TREATMENT INVOICE"}
                          </div>
                        </div>
                        <div className="w30Per flex1-end">
                          <div className="flex-column">
                            <div className="font-bold blue-color fs16">
                              {clinicDet?.name ?? profile?.name ?? ""}
                            </div>
                            <div className="header-right-text2">
                              {clinicDet?.address ?? ""}
                            </div>
                            {clinicDet?.taxId?.length > 0 ? (
                              <div className="flex-row-ali-cen">
                                <div className="font-bold fs12 blue-color">
                                  {/* CLINIC  */}
                                  TAX ID:{" "}
                                </div>
                                <div className="header-right-text2 ml3">
                                  {clinicDet?.taxId ?? ""}
                                </div>
                              </div>
                            ) : (
                              ""
                            )}
                            {clinicDet?.mobile?.length > 0 ? (
                              <div className="flex-row-ali-cen">
                                <div className="font-bold fs12 blue-color">
                                  Contact:{" "}
                                </div>
                                <div className="header-right-text2 ml3">
                                  {clinicDet?.mobile ?? ""}
                                </div>
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="line" />
                      <div className="flex-row mt10 ph10">
                        <div className="w60Per">
                          <div className="flex-row-ali-cen">
                            <Typography className="font-bold fs14 gray4">
                              {selectedBill?.petName?.length > 0
                                ? "Pet / "
                                : ""}
                              Parent Name
                            </Typography>
                            <div className="txt-semi-bold fs14 black capitalize ml10">
                              {selectedBill?.petName?.length > 0
                                ? `${selectedBill?.petName} / `
                                : ""}
                              {selectedBill?.userName}
                            </div>
                          </div>
                          {selectedBill?.mobile?.length > 0 ? (
                            <div className="flex-row-ali-cen">
                              <Typography className="font-bold fs14 gray4">
                                Phone Number
                              </Typography>
                              <div className="txt-semi-bold fs14 black capitalize ml10">
                                {selectedBill?.mobile}
                              </div>
                            </div>
                          ) : null}
                          {selectedBill?.apartmentAddress?.length > 0 ? (
                            <div className="flex-row">
                              <Typography className="font-bold fs14 gray4">
                                Address
                              </Typography>
                              <div className="txt-semi-bold fs14 black capitalize ml10 w50Per flex-row-wrap">
                                {selectedBill?.apartmentAddress}
                              </div>
                            </div>
                          ) : null}
                          <div className="flex-row">
                            <Typography className="font-bold fs14 gray4">
                              Invoice Number
                            </Typography>
                            <div className="txt-semi-bold fs14 black capitalize ml10 w50Per flex-row-wrap">
                              {selectedBill?.treatmentInvoiceNumber ?? ""}
                            </div>
                          </div>
                          <div className="flex-row">
                            <Typography className="font-bold fs14 gray4">
                              Invoice Date
                            </Typography>
                            <div className="txt-semi-bold fs14 black capitalize ml10 w50Per flex-row-wrap">
                              {moment(
                                new Date(selectedBill?.createdDate),
                              ).format("DD MMM YYYY")}
                            </div>
                          </div>
                          {selectedBill?.doctorName?.length > 0 ? (
                            <div className="flex-row-ali-cen">
                              <Typography className="font-bold fs14 gray4">
                                Vet Name
                              </Typography>
                              <div className="txt-semi-bold fs14 black capitalize ml10">
                                {getRightSaluation(
                                  clinicVets,
                                  selectedBill?.doctorId,
                                )}
                                {selectedBill?.doctorName}
                              </div>
                            </div>
                          ) : null}
                        </div>
                        {selectedBill?.treatmentInvoiceNumber?.length > 0 ? (
                          <div className="flex1-end w40Per">
                            <div className="flex-column">
                              {selectedBill?.checkOut === "Y" ? (
                                <div className="flex-row-ali-cen">
                                  <Typography className="font-bold fs14 gray4">
                                    {selectedBill?.status !== "cancelled"
                                      ? "Payment "
                                      : ""}
                                    Status
                                  </Typography>
                                  <Typography
                                    className={`ml10 fs16 font-bold capitalize ${
                                      selectedBill?.status === "paid"
                                        ? "green2"
                                        : selectedBill?.status === "unpaid"
                                          ? "red2"
                                          : "orange-color"
                                    }`}
                                  >
                                    {selectedBill?.status === "partiallypaid"
                                      ? "Partially Paid"
                                      : selectedBill?.status}
                                  </Typography>
                                </div>
                              ) : null}
                              <div className="flex-row-ali-cen">
                                <Typography className="font-bold fs14 gray4">
                                  Total
                                </Typography>
                                <Typography className="black fs16 font-bold ml10">
                                  Rs{" "}
                                  {Number(
                                    roundWithSignedDiff(
                                      getTaxAndSubTotalAndTotal(treatTabList)
                                        ?.subTotal,
                                    )?.rounded,
                                  )?.toFixed(2)}
                                </Typography>
                              </div>
                              <div className="flex-row-ali-cen">
                                <Typography className="font-bold fs14 gray4">
                                  {selectedBill?.checkOut === "Y"
                                    ? "Amount Paid"
                                    : "Unapplied Credit"}
                                </Typography>
                                <Typography
                                  className={`${
                                    selectedBill?.checkOut === "Y"
                                      ? "green-color"
                                      : "yellow-color"
                                  } fs16 font-bold ml10`}
                                >
                                  Rs{" "}
                                  {Number(selectedBill?.amtPaid) > 0
                                    ? Number(selectedBill?.amtPaid)?.toFixed(2)
                                    : Number(selectedBill?.amtPaid)}
                                </Typography>
                              </div>
                              {selectedBill?.checkOut === "Y" ? (
                                <div className="flex-row-ali-cen">
                                  <Typography className="font-bold fs14 gray4">
                                    Balance Due
                                  </Typography>
                                  <Typography className="yellow-color fs16 font-bold ml10">
                                    Rs{" "}
                                    {Number(
                                      selectedBill?.balanceDue ?? 0,
                                    )?.toFixed(2)}
                                  </Typography>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <div>
                          <div className="txt-semi-bold fs14 black mt10 ph10">
                            INVOICE SUMMARY
                          </div>
                          <div className="thin-line mt10" />

                          <table className="custom-table">
                            <thead>
                              <tr>
                                <th className="w5Per">S.NO.</th>
                                <th className="w7Per">HSN/SAC Code</th>
                                <th className="w18Per">SERVICE / ITEM NAME</th>
                                <th className="w10Per">BATCH NO.</th>
                                <th className="w10Per">EXPIRY</th>
                                <th className="w10Per">QTY</th>
                                <th className="w10Per">UNIT PRICE</th>
                                {treatTabList?.some(
                                  (li) => li?.discount > 0,
                                ) ? (
                                  <th className="w10Per">UNIT DISCOUNT (Rs)</th>
                                ) : null}
                                <th className="w10Per">TAX</th>
                                <th className="w10Per">TOTAL</th>
                              </tr>
                            </thead>
                            <tbody>
                              {treatTabList?.length > 0 &&
                                treatTabList
                                  ?.filter((pd) => pd?.type === "consultation")
                                  ?.map((con, i) => (
                                    <tr className="billing-invoice-line-row">
                                      <td>{i + 1}</td>
                                      <td>-</td>
                                      <td>
                                        <div className="flex-row-ali-cen">
                                          Consultation (
                                          {con?.name === "Home"
                                            ? "Home Visit"
                                            : con?.name}
                                          ){" "}
                                          {selectedBill?.doctorName?.length > 0
                                            ? " - "
                                            : ""}
                                          <div className="font-bold fs12 black ml3">
                                            {selectedBill?.doctorName?.length >
                                            0
                                              ? `${getRightSaluation(
                                                  clinicVets,
                                                  selectedBill?.doctorId,
                                                )}${selectedBill?.doctorName}`
                                              : ""}
                                          </div>
                                          {selectedBill?.registrationNo
                                            ?.length > 0 ? (
                                            <div className="flex-row-ali-cen">
                                              <div className="font-regular fs12 black ml5">
                                                (
                                              </div>
                                              <div className="font-bold fs12 black ml2">
                                                RegNo:
                                              </div>
                                              <div className="font-regular fs12 black ml5 capitalize">
                                                {selectedBill?.registrationNo ??
                                                  selectedBill?.registrationNo}{" "}
                                                )
                                              </div>
                                            </div>
                                          ) : null}
                                        </div>
                                      </td>
                                      <td>-</td>
                                      <td>-</td>
                                      <td>1</td>
                                      <td>{Number(con?.price)?.toFixed(2)}</td>
                                      {treatTabList?.some(
                                        (li) => li?.discount > 0,
                                      ) ? (
                                        <td>
                                          {con?.discount > 0
                                            ? Number(con?.discount)?.toFixed(2)
                                            : "-"}
                                        </td>
                                      ) : null}
                                      <td>
                                        {con?.tax > 0
                                          ? `${Number(con?.tax)?.toFixed(2)}`
                                          : "-"}
                                      </td>
                                      <td>{Number(con?.total)?.toFixed(2)}</td>
                                    </tr>
                                  ))}

                              {treatTabList?.length > 0 &&
                                reorderList(treatTabList)
                                  ?.filter(
                                    (pd) => {
                                      const isProcedureSubItem =
                                        (pd?.procedureId ?? "")?.toString()
                                          ?.length > 0 &&
                                        !pd?.mainProcedure &&
                                        pd?.linked !== "N";
                                      const nonCon =
                                        pd?.type !== "consultation";
                                      const nonConsum =
                                        pd?.type?.toLowerCase() !==
                                        "consumables";
                                      return nonCon &&
                                        nonConsum &&
                                        selectedBill?.showSublineItems
                                        ? true
                                        : !isProcedureSubItem;
                                    },
                                    // selectedBill?.showSublineItems
                                    //   ? true
                                    //   : pd?.patientWorkbook
                                    //     ? pd?.mainProcedure
                                    //     : true,
                                  )
                                  ?.map((itm, i) => {
                                    const isSubItem =
                                      (itm?.procedureId ?? "")?.toString()
                                        ?.length > 0 &&
                                      !itm?.mainProcedure &&
                                      itm?.linked !== "N";
                                    const showSubPrice =
                                      selectedBill?.showSublinePrice;
                                    const reqTotal = showSubPrice
                                      ? (itm?.total ?? 0)
                                      : itm?.list?.length > 0
                                        ? Number(
                                            Number(
                                              itm?.list
                                                ?.filter((li) =>
                                                  selectedBill?.showSublinePrice
                                                    ? li?.type?.toLowerCase() !==
                                                      "consumables"
                                                    : true,
                                                )
                                                ?.reduce(
                                                  (acc, li) =>
                                                    acc + Number(li?.total),
                                                  0,
                                                ) ?? 0,
                                            ) + Number(itm?.total ?? 0),
                                          )
                                        : (itm?.total ?? 0);
                                    const reqUnitPrice = showSubPrice
                                      ? (itm?.mrpPrice ?? itm?.price ?? 0)
                                      : itm?.list?.length > 0
                                        ? Number(
                                            Number(
                                              itm?.list
                                                ?.filter((li) =>
                                                  selectedBill?.showSublinePrice
                                                    ? li?.type?.toLowerCase() !==
                                                      "consumables"
                                                    : true,
                                                )
                                                ?.reduce(
                                                  (acc, li) =>
                                                    acc +
                                                    Number(
                                                      li?.mrpPrice ?? li?.price,
                                                    ),
                                                  0,
                                                ) ?? 0,
                                            ) +
                                              Number(
                                                itm?.mrpPrice ??
                                                  itm?.price ??
                                                  0,
                                              ),
                                          )
                                        : (itm?.mrpPrice ?? itm?.price ?? 0);

                                    const reqDiscount = showSubPrice
                                      ? (itm?.discount ?? 0)
                                      : itm?.list?.length > 0
                                        ? Number(
                                            Number(
                                              itm?.list
                                                ?.filter((li) =>
                                                  selectedBill?.showSublinePrice
                                                    ? li?.type?.toLowerCase() !==
                                                      "consumables"
                                                    : true,
                                                )
                                                ?.reduce(
                                                  (acc, li) =>
                                                    acc + Number(li?.discount),
                                                  0,
                                                ) ?? 0,
                                            ) + Number(itm?.discount ?? 0),
                                          )
                                        : (itm?.discount ?? 0);
                                    if (
                                      isSubItem &&
                                      !selectedBill?.showSublineItems
                                    ) {
                                      return null;
                                    }

                                    return (
                                      <>
                                        <tr
                                          key={`${itm?.paymentDetailId}_${i}`}
                                          className="billing-invoice-line-row"
                                        >
                                          <td>{i + 1}</td>
                                          <td>
                                            <div
                                              className={
                                                isSubItem
                                                  ? ""
                                                  : "fs12 font-bold black"
                                              }
                                            >
                                              {itm?.hsnSacCode?.length > 0
                                                ? itm?.hsnSacCode
                                                : "-"}
                                            </div>
                                          </td>
                                          <td>
                                            <div
                                              className={
                                                isSubItem
                                                  ? ""
                                                  : "billing-invoice-item-cell"
                                              }
                                            >
                                              <div
                                                className={
                                                  isSubItem
                                                    ? ""
                                                    : "fs12 font-bold black"
                                                }
                                              >
                                                {itm?.serviceId
                                                  ? removeDuplicateBracketText(
                                                      itm?.tradeName ??
                                                        itm?.name,
                                                    )
                                                  : getNameWithValue(itm)}
                                              </div>
                                              {itm?.stockId ? (
                                                <div>
                                                  {/* <div className="font-medium fs10 gray4">
                                                    Generic Name:{" "}
                                                    {itm?.name ?? ""}
                                                  </div> */}
                                                  {itm?.drugSchedule?.length >
                                                  0 ? (
                                                    <div className="fs10 gray4 font-medium">
                                                      Drug Schedule:{" "}
                                                      {itm?.drugSchedule ?? ""}
                                                    </div>
                                                  ) : null}
                                                </div>
                                              ) : null}
                                            </div>
                                          </td>
                                          <td>
                                            <div
                                              className={
                                                isSubItem
                                                  ? ""
                                                  : "fs12 font-bold black"
                                              }
                                            >
                                              {itm?.stockId &&
                                              !itm?.autoConsumed &&
                                              itm?.batchNo?.length > 0
                                                ? itm?.batchNo
                                                : "-"}
                                            </div>
                                          </td>
                                          <td>
                                            <div
                                              className={
                                                isSubItem
                                                  ? ""
                                                  : "fs12 font-bold black"
                                              }
                                            >
                                              {itm?.stockId &&
                                              !itm?.autoConsumed &&
                                              itm?.expiryDate?.length > 0
                                                ? moment(
                                                    new Date(itm?.expiryDate),
                                                  ).format("DD MMM YYYY")
                                                : "-"}
                                            </div>
                                          </td>
                                          <td>
                                            <div
                                              className={
                                                isSubItem
                                                  ? ""
                                                  : "fs12 font-bold black"
                                              }
                                            >
                                              {itm?.quantity}
                                              {itm?.stockId &&
                                              itm?.uomName?.length > 0
                                                ? `${getRightPackageUom(
                                                    itm?.uomName,
                                                    true,
                                                  )}`
                                                : ""}
                                            </div>
                                          </td>
                                          <td>
                                            {isSubItem &&
                                            !showSubPrice ? null : (
                                              <div
                                                className={
                                                  isSubItem
                                                    ? ""
                                                    : "fs12 font-bold black"
                                                }
                                              >
                                                {Number(
                                                  !showSubPrice
                                                    ? reqTotal
                                                    : reqUnitPrice,
                                                )?.toFixed(2)}
                                              </div>
                                            )}
                                          </td>
                                          {treatTabList?.some(
                                            (li) => li?.discount > 0,
                                          ) ? (
                                            <td>
                                              {isSubItem &&
                                              !showSubPrice ? null : (
                                                <div
                                                  className={
                                                    isSubItem
                                                      ? ""
                                                      : "fs12 font-bold black"
                                                  }
                                                >
                                                  {Number(reqDiscount) > 0
                                                    ? Number(
                                                        reqDiscount,
                                                      )?.toFixed(2)
                                                    : "-"}
                                                </div>
                                              )}
                                            </td>
                                          ) : null}
                                          <td>
                                            {isSubItem &&
                                            !showSubPrice ? null : (
                                              <div
                                                className={
                                                  isSubItem
                                                    ? ""
                                                    : "fs12 font-bold black"
                                                }
                                              >
                                                {itm?.tax > 0
                                                  ? `${Number(
                                                      itm?.tax,
                                                    )?.toFixed(2)}`
                                                  : "-"}
                                              </div>
                                            )}
                                          </td>
                                          <td>
                                            {isSubItem &&
                                            !showSubPrice ? null : (
                                              <div
                                                className={
                                                  isSubItem
                                                    ? ""
                                                    : "fs12 font-bold black"
                                                }
                                              >
                                                {Number(reqTotal)?.toFixed(2)}
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      </>
                                    );
                                  })}
                              {selectedBill?.showSublineItems &&
                                reorderList(treatTabList)?.filter(
                                  (pd) =>
                                    pd?.type?.toLowerCase() === "consumables",
                                )?.length > 0 &&
                                reorderList(treatTabList)
                                  ?.filter(
                                    (pd) =>
                                      pd?.type?.toLowerCase() === "consumables",
                                  )
                                  ?.slice(0, 1)
                                  ?.map((itm, i) => {
                                    const reqTotal =
                                      reorderList(treatTabList)
                                        ?.filter(
                                          (pd) =>
                                            pd?.type?.toLowerCase() ===
                                            "consumables",
                                        )
                                        ?.reduce(
                                          (acc, li) => acc + Number(li?.total),
                                          0,
                                        ) ?? 0;

                                    const reqUnitPrice =
                                      reorderList(treatTabList)
                                        ?.filter(
                                          (pd) =>
                                            pd?.type?.toLowerCase() ===
                                            "consumables",
                                        )
                                        ?.reduce(
                                          (acc, li) =>
                                            acc +
                                            Number(
                                              Number(
                                                li?.mrpPrice ?? li?.price ?? 0,
                                              ) * Number(li?.quantity ?? 1),
                                            ),
                                          0,
                                        ) ?? 0;

                                    const reqDiscount =
                                      reorderList(treatTabList)
                                        ?.filter(
                                          (pd) =>
                                            pd?.type?.toLowerCase() ===
                                            "consumables",
                                        )
                                        ?.reduce(
                                          (acc, li) =>
                                            acc + Number(li?.discount),
                                          0,
                                        ) ?? 0;

                                    return (
                                      <>
                                        <tr
                                          key={i}
                                          className="billing-invoice-line-row"
                                        >
                                          <td>
                                            <div className="fs12 font-bold black">
                                              -
                                            </div>
                                          </td>
                                          <td>
                                            <div className="fs12 font-bold black">
                                              Medical Consumables
                                            </div>
                                          </td>
                                          <td>
                                            <div className="fs12 font-bold black">
                                              -
                                            </div>
                                          </td>
                                          <td>
                                            <div className="fs12 font-bold black">
                                              -
                                            </div>
                                          </td>
                                          <td>
                                            <div className="fs12 font-bold black">
                                              1
                                            </div>
                                          </td>
                                          <td>
                                            {selectedBill?.showSublinePrice ? (
                                              <div className="fs12 font-bold black">
                                                {Number(reqUnitPrice)?.toFixed(
                                                  2,
                                                )}
                                              </div>
                                            ) : null}
                                          </td>
                                          {treatTabList?.some(
                                            (li) =>
                                              li?.discount > 0 &&
                                              selectedBill?.showSublinePrice,
                                          ) ? (
                                            <td>
                                              <div className="fs12 font-bold black">
                                                {Number(reqDiscount) > 0
                                                  ? Number(
                                                      reqDiscount,
                                                    )?.toFixed(2)
                                                  : "-"}
                                              </div>
                                            </td>
                                          ) : null}
                                          <td>
                                            {selectedBill?.showSublinePrice ? (
                                              <div className="fs12 font-bold black">
                                                {Number(
                                                  getTaxAndSubTotalAndTotal(
                                                    treatTabList?.filter(
                                                      (pd) =>
                                                        pd?.type?.toLowerCase() ===
                                                        "consumables",
                                                    ),
                                                  )?.tax ?? 0,
                                                ) > 0
                                                  ? Number(
                                                      getTaxAndSubTotalAndTotal(
                                                        treatTabList?.filter(
                                                          (pd) =>
                                                            pd?.type?.toLowerCase() ===
                                                            "consumables",
                                                        ),
                                                      )?.tax,
                                                    )?.toFixed(2)
                                                  : "-"}
                                              </div>
                                            ) : null}
                                          </td>
                                          <td>
                                            {selectedBill?.showSublinePrice ? (
                                              <div className="fs12 font-bold black">
                                                {Number(reqTotal)?.toFixed(2)}
                                              </div>
                                            ) : null}
                                          </td>
                                        </tr>
                                      </>
                                    );
                                  })}
                            </tbody>
                          </table>
                          <InvoiceBillFooter
                            billedByName={selectedBill?.billedByName ?? ""}
                            tabList={treatTabList}
                            subtotalLabel="Treatment Subtotal"
                            disclaimer={clinicDet?.disclaimer}
                          />
                          {/* {isDotMatrixPrint ? null : (
                        <div className="flex-row mv20 ph10">
                          <img
                            src={require("../../../assets/images/jpg/WoofQrCode.jpeg")}
                            alt=""
                            className="img-h50"
                          />
                          <div className="blu-back wh50 flex-center">
                            <img
                              src={require("../../../assets/images/png/VetInstantLogo.png")}
                              alt=""
                              className="img-h40"
                            />
                          </div>
                          <div className="flex-center">
                            <div className="font-bold fs14 ml20">
                              Scan to download our app
                            </div>
                          </div>
                        </div>
                      )} */}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {resolvedBillsToShow?.includes("treatment") &&
                  selectedBill?.status !== "cancelled" &&
                  Number(selectedBill?.amtPaid) > 0 &&
                  getTabBasedCollections("treatment")?.length > 0 ? (
                    <div
                      className={`billing-payment-summary-section${
                        selectedBill?.status === "cancelled"
                          ? " billing-invoice-section-break"
                          : ""
                      }`}
                    >
                      <div className="ph10">
                        <div className="txt-semi-bold fs14 black mt30 ph10">
                          PAYMENT SUMMARY
                        </div>
                        <div className="thin-line mt10" />
                        <table className="custom-table">
                          {getPaySumTableHead()}
                          <tbody>
                            {getTabBasedCollectionsWithBalance(
                              "treatment",
                            )?.map((tl, i) => {
                              return (
                                <tr key={`treat_${tl?.collectionId}_${i}`}>
                                  <td>
                                    {moment(new Date(tl?.receiptDate)).format(
                                      "DD MMM YYYY",
                                    )}
                                  </td>
                                  <td>
                                    {tl?.receiptNo ??
                                      selectedBill?.treatmentInvoiceNumber ??
                                      ""}
                                  </td>
                                  <td>
                                    {Number(tl?.balanceBefore ?? 0)?.toFixed(2)}
                                  </td>
                                  <td>
                                    {tl?.paymentMode === "advanceAdjustment"
                                      ? "Advance Adjustment"
                                      : tl?.paymentMode}
                                  </td>
                                  <td>
                                    {Number(tl?.amountPaid ?? 0) > 0
                                      ? Number(tl?.amountPaid ?? 0)?.toFixed(2)
                                      : "Nil"}
                                  </td>
                                  <td>
                                    {Number(tl?.balanceAfter ?? 0) > 0
                                      ? Number(tl?.balanceAfter ?? 0)?.toFixed(
                                          2,
                                        )
                                      : "Nil"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div className="thin-line" />
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
              {hasPharmacyBill ? (
                <div
                  className={
                    showTreatmentBill ? "billing-invoice-section-break" : ""
                  }
                >
                  <div className="mt30">
                    <div className="flex-row-ali-cen mv20">
                      <div className="w30Per">
                        {isDotMatrixPrint ? null : (
                          <div className="pl20">
                            <div>
                              {clinicDet?.image ? (
                                <img
                                  src={clinicDet?.image}
                                  className="img-h75"
                                  alt=""
                                />
                              ) : (
                                <div className="flex-center h50img">
                                  <Typography className="font-bold fs30 white-color capitalize">
                                    {clinicDet?.name?.[0]}
                                  </Typography>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="w40Per flex-center">
                        <div className="font-bold fs18 blue-color">
                          {isBillEstimate
                            ? "BILL ESTIMATE"
                            : selectedBill?.checkOut !== "Y"
                              ? "PROFORMA INVOICE"
                              : isBillCombined
                                ? "TAX INVOICE"
                                : `${
                                    selectedBill?.type === "Pharmacy" ||
                                    selectedBill?.type === "Pet Shop"
                                      ? "TAX"
                                      : "PHARMACY"
                                  } INVOICE`}
                        </div>
                      </div>
                      <div className="w30Per flex1-end">
                        <div className="flex-column">
                          <div className="font-bold blue-color fs16">
                            {(selectedBill?.type === "Pharmacy"
                              ? (clinicDet?.pharmacyName ?? "")
                              : selectedBill?.type === "Pet Shop"
                                ? (clinicDet?.petShopName ?? "")
                                : (clinicDet?.pharmacyName ?? "")) ??
                              clinicDet?.name ??
                              ""}
                          </div>
                          <div className="header-right-text2">
                            {(selectedBill?.type === "Pharmacy"
                              ? (clinicDet?.pharmacyAddress ?? "")
                              : selectedBill?.type === "Pet Shop"
                                ? (clinicDet?.petShopAddress ?? "")
                                : (clinicDet?.pharmacyAddress ?? "")) ??
                              clinicDet?.address ??
                              ""}
                          </div>
                          {clinicDet?.pharmacyTaxId?.length > 0 ||
                          clinicDet?.taxId?.length > 0 ||
                          clinicDet?.petShopTaxId?.length > 0 ? (
                            <div className="flex-row-ali-cen">
                              <div className="font-bold fs12 blue-color">
                                TAX ID:{" "}
                              </div>
                              <div className="header-right-text2 ml3">
                                {(selectedBill?.type === "Pet Shop"
                                  ? clinicDet?.petShopTaxId
                                  : clinicDet?.pharmacyTaxId) ??
                                  clinicDet?.taxId ??
                                  ""}{" "}
                              </div>
                            </div>
                          ) : (
                            ""
                          )}
                          {clinicDet?.mobile?.length > 0 ? (
                            <div className="flex-row-ali-cen">
                              <div className="font-bold fs12 blue-color">
                                Contact:{" "}
                              </div>
                              <div className="header-right-text2 ml3">
                                {clinicDet?.mobile ?? ""}
                              </div>
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="line" />
                    <div className="flex-row mt10 ph10">
                      <div className="w60Per">
                        <div className="flex-row-ali-cen">
                          <Typography className="font-bold fs14 gray4">
                            {selectedBill?.petName?.length > 0 ? "Pet / " : ""}
                            Parent Name
                          </Typography>
                          <div className="txt-semi-bold fs14 black capitalize ml10">
                            {selectedBill?.petName?.length > 0
                              ? `${selectedBill?.petName} / `
                              : ""}
                            {selectedBill?.userName}
                          </div>
                        </div>
                        {selectedBill?.mobile?.length > 0 ? (
                          <div className="flex-row-ali-cen">
                            <Typography className="font-bold fs14 gray4">
                              Phone Number
                            </Typography>
                            <div className="txt-semi-bold fs14 black capitalize ml10">
                              {selectedBill?.mobile}
                            </div>
                          </div>
                        ) : null}
                        {selectedBill?.apartmentAddress?.length > 0 ? (
                          <div className="flex-row">
                            <Typography className="font-bold fs14 gray4">
                              Address
                            </Typography>
                            <div className="txt-semi-bold fs14 black capitalize ml10 w50Per flex-row-wrap">
                              {selectedBill?.apartmentAddress}
                            </div>
                          </div>
                        ) : null}
                        <div className="flex-row">
                          <Typography className="font-bold fs14 gray4">
                            Invoice Number
                          </Typography>
                          <div className="txt-semi-bold fs14 black capitalize ml10 w50Per flex-row-wrap">
                            {selectedBill?.treatmentInvoiceNumber ?? ""}
                          </div>
                        </div>
                        <div className="flex-row">
                          <Typography className="font-bold fs14 gray4">
                            Invoice Date
                          </Typography>
                          <div className="txt-semi-bold fs14 black capitalize ml10 w50Per flex-row-wrap">
                            {moment(new Date(selectedBill?.createdDate)).format(
                              "DD MMM YYYY",
                            )}
                          </div>
                        </div>
                        {selectedBill?.doctorName?.length > 0 ? (
                          <div className="flex-row-ali-cen">
                            <Typography className="font-bold fs14 gray4">
                              Vet Name
                            </Typography>
                            <div className="txt-semi-bold fs14 black capitalize ml10">
                              {getRightSaluation(
                                clinicVets,
                                selectedBill?.doctorId,
                              )}
                              {selectedBill?.doctorName}
                            </div>
                          </div>
                        ) : null}
                      </div>
                      {selectedBill?.treatmentInvoiceNumber?.length > 0 ? (
                        <div className="flex1-end w40Per">
                          <div className="flex-column">
                            {selectedBill?.checkOut === "Y" ? (
                              <div className="flex-row-ali-cen">
                                <Typography className="font-bold fs14 gray4">
                                  {selectedBill?.status !== "cancelled"
                                    ? "Payment "
                                    : ""}
                                  Status
                                </Typography>
                                <Typography
                                  className={`ml10 fs16 font-bold capitalize ${
                                    selectedBill?.status === "paid"
                                      ? "green2"
                                      : selectedBill?.status === "unpaid"
                                        ? "red2"
                                        : "orange-color"
                                  }`}
                                >
                                  {selectedBill?.status === "partiallypaid"
                                    ? "Partially Paid"
                                    : selectedBill?.status}
                                </Typography>
                              </div>
                            ) : null}
                            <div className="flex-row-ali-cen">
                              <Typography className="font-bold fs14 gray4">
                                Total
                              </Typography>
                              <Typography className="black fs16 font-bold ml10">
                                Rs{" "}
                                {Number(
                                  roundWithSignedDiff(
                                    getTaxAndSubTotalAndTotal(pharTabList)
                                      ?.subTotal,
                                  )?.rounded,
                                )?.toFixed(2)}
                              </Typography>
                            </div>
                            <div className="flex-row-ali-cen">
                              <Typography className="font-bold fs14 gray4">
                                {selectedBill?.checkOut === "Y"
                                  ? "Amount Paid"
                                  : "Unapplied Credit"}
                              </Typography>
                              <Typography
                                className={`${
                                  selectedBill?.checkOut === "Y"
                                    ? "green-color"
                                    : "yellow-color"
                                } fs16 font-bold ml10`}
                              >
                                Rs{" "}
                                {Number(selectedBill?.amtPaid) > 0
                                  ? Number(selectedBill?.amtPaid)?.toFixed(2)
                                  : Number(selectedBill?.amtPaid)}
                              </Typography>
                            </div>
                            {selectedBill?.checkOut === "Y" ? (
                              <div className="flex-row-ali-cen">
                                <Typography className="font-bold fs14 gray4">
                                  Balance Due
                                </Typography>
                                <Typography className="yellow-color fs16 font-bold ml10">
                                  Rs{" "}
                                  {Number(
                                    selectedBill?.balanceDue ?? 0,
                                  )?.toFixed(2)}
                                </Typography>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <div>
                        <div className="txt-semi-bold fs14 black mt10 ph10">
                          INVOICE SUMMARY
                        </div>
                        <div className="thin-line mt10" />
                        <table className="custom-table">
                          <thead>
                            <tr>
                              <th className="w5Per">S.NO.</th>
                              <th className="w7Per">HSN/SAC Code</th>
                              <th className="w18Per">SERVICE / ITEM NAME</th>
                              <th className="w10Per">BATCH NO.</th>
                              <th className="w10Per">EXPIRY</th>
                              <th className="w10Per">QTY</th>
                              <th className="w10Per">UNIT PRICE</th>
                              {pharTabList?.some((li) => li?.discount > 0) ? (
                                <th className="w10Per">UNIT DISCOUNT (Rs)</th>
                              ) : null}
                              <th className="w10Per">TAX</th>
                              <th className="w10Per">TOTAL</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pharTabList?.length > 0 &&
                              pharTabList
                                ?.filter((pd) => pd?.type !== "consultation")
                                ?.map((itm, i) => {
                                  return (
                                    <tr
                                      key={`${itm?.paymentDetailId}_${i}`}
                                      className="billing-invoice-line-row"
                                    >
                                      <td>{i + 1}</td>
                                      <td>
                                        <div className="fs12 font-bold black">
                                          {itm?.hsnSacCode?.length > 0
                                            ? itm?.hsnSacCode
                                            : "-"}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="billing-invoice-item-cell">
                                          <div className="fs12 font-bold black">
                                            {itm?.serviceId
                                              ? removeDuplicateBracketText(
                                                  itm?.tradeName ?? itm?.name,
                                                )
                                              : getNameWithValue(itm)}
                                          </div>
                                          {itm?.stockId ? (
                                            <div>
                                              {/* <div className="font-medium fs10 gray4">
                                                Generic Name: {itm?.name ?? ""}
                                              </div> */}
                                              {itm?.drugSchedule?.length > 0 ? (
                                                <div className="fs10 gray4 font-medium">
                                                  Drug Schedule:{" "}
                                                  {itm?.drugSchedule ?? ""}
                                                </div>
                                              ) : null}
                                            </div>
                                          ) : null}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="fs12 font-bold black">
                                          {itm?.stockId &&
                                          !itm?.autoConsumed &&
                                          itm?.batchNo?.length > 0
                                            ? itm?.batchNo
                                            : "-"}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="fs12 font-bold black">
                                          {itm?.stockId &&
                                          !itm?.autoConsumed &&
                                          itm?.expiryDate?.length > 0
                                            ? moment(
                                                new Date(itm?.expiryDate),
                                              ).format("DD MMM YYYY")
                                            : "-"}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="fs12 font-bold black">
                                          {itm?.quantity}
                                          {itm?.stockId &&
                                          itm?.uomName?.length > 0
                                            ? `${getRightPackageUom(itm?.uomName, true)}`
                                            : ""}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="fs12 font-bold black">
                                          {Number(
                                            itm?.mrpPrice ?? itm?.price,
                                          )?.toFixed(2)}
                                        </div>
                                      </td>
                                      {pharTabList?.some(
                                        (li) => li?.discount > 0,
                                      ) ? (
                                        <td>
                                          <div className="fs12 font-bold black">
                                            {Number(
                                              itm?.mrpDiscount ?? itm?.discount,
                                            ) > 0
                                              ? Number(
                                                  itm?.mrpDiscount ??
                                                    itm?.discount,
                                                )?.toFixed(2)
                                              : "-"}
                                          </div>
                                        </td>
                                      ) : null}
                                      <td>
                                        <div className="fs12 font-bold black">
                                          {clinicDet?.pharmacyTaxId?.length > 0
                                            ? itm?.tax > 0
                                              ? `${Number(itm?.tax)?.toFixed(2)}`
                                              : "-"
                                            : "-"}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="fs12 font-bold black">
                                          {Number(itm?.total)?.toFixed(2)}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                          </tbody>
                        </table>
                        <InvoiceBillFooter
                          billedByName={selectedBill?.billedByName ?? ""}
                          tabList={pharTabList}
                          subtotalLabel={`${
                            selectedBill?.type === "Pharmacy" ||
                            selectedBill?.type === "Pet Shop"
                              ? ""
                              : "Pharmacy "
                          }Subtotal`}
                          disclaimer={clinicDet?.disclaimer}
                        />
                        {/* {isDotMatrixPrint ? null : (
                    <div className="flex-row mv20 ph10">
                      <img
                        src={require("../../../assets/images/jpg/WoofQrCode.jpeg")}
                        alt=""
                        className="img-h50"
                      />
                      <div className="blu-back wh50 flex-center">
                        <img
                          src={require("../../../assets/images/png/VetInstantLogo.png")}
                          alt=""
                          className="img-h40"
                        />
                      </div>
                      <div className="flex-center">
                        <div className="font-bold fs14 ml20">
                          Scan to download our app
                        </div>
                      </div>
                    </div>
                  )} */}
                      </div>
                    </div>
                  </div>
                  {resolvedBillsToShow?.includes("pharmacy") &&
                  selectedBill?.status !== "cancelled" &&
                  Number(selectedBill?.amtPaid) > 0 &&
                  getTabBasedCollections("pharmacy")?.length > 0 ? (
                    <div className="billing-payment-summary-section">
                      <div className="ph10">
                        <div className="txt-semi-bold fs14 black mt30 ph10">
                          PAYMENT SUMMARY
                        </div>
                        <div className="thin-line mt10" />
                        <table className="custom-table">
                          {getPaySumTableHead()}
                          <tbody>
                            {getTabBasedCollectionsWithBalance("pharmacy")?.map(
                              (tl, i) => {
                                return (
                                  <tr key={`phar_${tl?.collectionId}_${i}`}>
                                    <td>
                                      {moment(new Date(tl?.receiptDate)).format(
                                        "DD MMM YYYY",
                                      )}
                                    </td>
                                    <td>
                                      {tl?.receiptNo ??
                                        selectedBill?.treatmentInvoiceNumber ??
                                        ""}
                                    </td>
                                    <td>
                                      {Number(tl?.balanceBefore ?? 0)?.toFixed(
                                        2,
                                      )}
                                    </td>
                                    <td>
                                      {tl?.paymentMode === "advanceAdjustment"
                                        ? "Advance Adjustment"
                                        : tl?.paymentMode}
                                    </td>
                                    <td>
                                      {Number(tl?.amountPaid ?? 0) > 0
                                        ? Number(tl?.amountPaid ?? 0)?.toFixed(
                                            2,
                                          )
                                        : "Nil"}
                                    </td>
                                    <td>
                                      {Number(tl?.balanceAfter ?? 0) > 0
                                        ? Number(
                                            tl?.balanceAfter ?? 0,
                                          )?.toFixed(2)
                                        : "Nil"}
                                    </td>
                                  </tr>
                                );
                              },
                            )}
                          </tbody>
                        </table>
                        <div className="thin-line" />
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {hasPetShopBill ? (
                <div
                  className={
                    showTreatmentBill || hasPharmacyBill
                      ? "billing-invoice-section-break"
                      : ""
                  }
                >
                  <div className="mt30">
                    <div className="flex-row-ali-cen mv20">
                      <div className="w30Per">
                        {isDotMatrixPrint ? null : (
                          <div className="pl20">
                            <div>
                              {clinicDet?.image ? (
                                <img
                                  src={clinicDet?.image}
                                  className="img-h75"
                                  alt=""
                                />
                              ) : (
                                <div className="flex-center h50img">
                                  <Typography className="font-bold fs30 white-color capitalize">
                                    {clinicDet?.name?.[0]}
                                  </Typography>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="w40Per flex-center">
                        <div className="font-bold fs18 blue-color">
                          {isBillEstimate
                            ? "BILL ESTIMATE"
                            : selectedBill?.checkOut !== "Y"
                              ? "PROFORMA INVOICE"
                              : "TAX INVOICE"}
                        </div>
                      </div>
                      <div className="w30Per flex1-end">
                        <div className="flex-column">
                          <div className="font-bold blue-color fs16">
                            {clinicDet?.petShopName ??
                              profile?.petShopName ??
                              clinicDet?.name ??
                              ""}
                          </div>
                          <div className="header-right-text2">
                            {clinicDet?.petShopAddress ??
                              clinicDet?.address ??
                              ""}
                          </div>
                          {clinicDet?.petShopTaxId?.length > 0 ||
                          clinicDet?.taxId?.length > 0 ? (
                            <div className="flex-row-ali-cen">
                              <div className="font-bold fs12 blue-color">
                                TAX ID:
                              </div>
                              <div className="header-right-text2 ml3">
                                {clinicDet?.petShopTaxId ??
                                  clinicDet?.taxId ??
                                  ""}
                              </div>
                            </div>
                          ) : (
                            ""
                          )}
                          {clinicDet?.mobile?.length > 0 ? (
                            <div className="flex-row-ali-cen">
                              <div className="font-bold fs12 blue-color">
                                Contact:{" "}
                              </div>
                              <div className="header-right-text2 ml3">
                                {clinicDet?.mobile ?? ""}
                              </div>
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="line" />
                    <div className="flex-row mt10 ph10">
                      <div className="w60Per">
                        <div className="flex-row-ali-cen">
                          <Typography className="font-bold fs14 gray4">
                            {selectedBill?.petName?.length > 0 ? "Pet / " : ""}
                            Parent Name
                          </Typography>
                          <div className="txt-semi-bold fs14 black capitalize ml10">
                            {selectedBill?.petName?.length > 0
                              ? `${selectedBill?.petName} / `
                              : ""}
                            {selectedBill?.userName}
                          </div>
                        </div>
                        {selectedBill?.mobile?.length > 0 ? (
                          <div className="flex-row-ali-cen">
                            <Typography className="font-bold fs14 gray4">
                              Phone Number
                            </Typography>
                            <div className="txt-semi-bold fs14 black capitalize ml10">
                              {selectedBill?.mobile}
                            </div>
                          </div>
                        ) : null}
                        {selectedBill?.apartmentAddress?.length > 0 ? (
                          <div className="flex-row">
                            <Typography className="font-bold fs14 gray4">
                              Address
                            </Typography>
                            <div className="txt-semi-bold fs14 black capitalize ml10 w50Per flex-row-wrap">
                              {selectedBill?.apartmentAddress}
                            </div>
                          </div>
                        ) : null}
                        <div className="flex-row">
                          <Typography className="font-bold fs14 gray4">
                            Invoice Number
                          </Typography>
                          <div className="txt-semi-bold fs14 black capitalize ml10 w50Per flex-row-wrap">
                            {selectedBill?.treatmentInvoiceNumber ?? ""}
                          </div>
                        </div>
                        <div className="flex-row">
                          <Typography className="font-bold fs14 gray4">
                            Invoice Date
                          </Typography>
                          <div className="txt-semi-bold fs14 black capitalize ml10 w50Per flex-row-wrap">
                            {moment(new Date(selectedBill?.createdDate)).format(
                              "DD MMM YYYY",
                            )}
                          </div>
                        </div>
                        {selectedBill?.doctorName?.length > 0 ? (
                          <div className="flex-row-ali-cen">
                            <Typography className="font-bold fs14 gray4">
                              Vet Name
                            </Typography>
                            <div className="txt-semi-bold fs14 black capitalize ml10">
                              {getRightSaluation(
                                clinicVets,
                                selectedBill?.doctorId,
                              )}
                              {selectedBill?.doctorName}
                            </div>
                          </div>
                        ) : null}
                      </div>
                      {selectedBill?.treatmentInvoiceNumber?.length > 0 ? (
                        <div className="flex1-end w40Per">
                          <div className="flex-column">
                            {selectedBill?.checkOut === "Y" ? (
                              <div className="flex-row-ali-cen">
                                <Typography className="font-bold fs14 gray4">
                                  {selectedBill?.status !== "cancelled"
                                    ? "Payment "
                                    : ""}
                                  Status
                                </Typography>
                                <Typography
                                  className={`ml10 fs16 font-bold capitalize ${
                                    selectedBill?.status === "paid"
                                      ? "green2"
                                      : selectedBill?.status === "unpaid"
                                        ? "red2"
                                        : "orange-color"
                                  }`}
                                >
                                  {selectedBill?.status === "partiallypaid"
                                    ? "Partially Paid"
                                    : selectedBill?.status}
                                </Typography>
                              </div>
                            ) : null}
                            <div className="flex-row-ali-cen">
                              <Typography className="font-bold fs14 gray4">
                                Total
                              </Typography>
                              <Typography className="black fs16 font-bold ml10">
                                Rs{" "}
                                {Number(
                                  roundWithSignedDiff(
                                    getTaxAndSubTotalAndTotal(petShopTabList)
                                      ?.subTotal,
                                  )?.rounded,
                                )?.toFixed(2)}
                              </Typography>
                            </div>
                            <div className="flex-row-ali-cen">
                              <Typography className="font-bold fs14 gray4">
                                {selectedBill?.checkOut === "Y"
                                  ? "Amount Paid"
                                  : "Unapplied Credit"}
                              </Typography>
                              <Typography
                                className={`${
                                  selectedBill?.checkOut === "Y"
                                    ? "green-color"
                                    : "yellow-color"
                                } fs16 font-bold ml10`}
                              >
                                Rs{" "}
                                {Number(selectedBill?.amtPaid) > 0
                                  ? Number(selectedBill?.amtPaid)?.toFixed(2)
                                  : Number(selectedBill?.amtPaid)}
                              </Typography>
                            </div>
                            {selectedBill?.checkOut === "Y" ? (
                              <div className="flex-row-ali-cen">
                                <Typography className="font-bold fs14 gray4">
                                  Balance Due
                                </Typography>
                                <Typography className="yellow-color fs16 font-bold ml10">
                                  Rs{" "}
                                  {Number(
                                    selectedBill?.balanceDue ?? 0,
                                  )?.toFixed(2)}
                                </Typography>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <div>
                        <div className="txt-semi-bold fs14 black mt10 ph10">
                          INVOICE SUMMARY
                        </div>
                        <div className="thin-line mt10" />

                        <table className="custom-table">
                          <thead>
                            <tr>
                              <th className="w5Per">S.NO.</th>
                              <th className="w7Per">HSN/SAC Code</th>
                              <th className="w18Per">SERVICE / ITEM NAME</th>
                              <th className="w10Per">BATCH NO.</th>
                              <th className="w10Per">EXPIRY</th>
                              <th className="w10Per">QTY</th>
                              <th className="w10Per">UNIT PRICE</th>
                              {petShopTabList?.some(
                                (li) => li?.discount > 0,
                              ) ? (
                                <th className="w10Per">UNIT DISCOUNT (Rs)</th>
                              ) : null}
                              <th className="w10Per">TAX</th>
                              <th className="w10Per">TOTAL</th>
                            </tr>
                          </thead>
                          <tbody>
                            {petShopTabList
                              ?.filter((li) => li?.quantity > 0)
                              ?.map((itm, i) => {
                                return (
                                  <tr
                                    key={`${itm?.paymentDetailId}_${i}`}
                                    className="billing-invoice-line-row"
                                  >
                                    <td>{i + 1}</td>
                                    <td>
                                      <div className="fs12 font-bold black">
                                        {itm?.hsnSacCode?.length > 0
                                          ? itm?.hsnSacCode
                                          : "-"}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="billing-invoice-item-cell">
                                        <div className="fs12 font-bold black">
                                          {itm?.serviceId
                                            ? removeDuplicateBracketText(
                                                itm?.tradeName ?? itm?.name,
                                              )
                                            : getNameWithValue(itm)}
                                        </div>
                                        {itm?.stockId ? (
                                          <div>
                                            {/* <div className="font-medium fs10 gray4">
                                              Generic Name: {itm?.name ?? ""}
                                            </div> */}
                                            {itm?.drugSchedule?.length > 0 ? (
                                              <div className="fs10 gray4 font-medium">
                                                Drug Schedule:{" "}
                                                {itm?.drugSchedule ?? ""}
                                              </div>
                                            ) : null}
                                          </div>
                                        ) : null}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="fs12 font-bold black">
                                        {itm?.stockId &&
                                        !itm?.autoConsumed &&
                                        itm?.batchNo?.length > 0
                                          ? itm?.batchNo
                                          : "-"}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="fs12 font-bold black">
                                        {itm?.stockId &&
                                        !itm?.autoConsumed &&
                                        itm?.expiryDate?.length > 0
                                          ? moment(
                                              new Date(itm?.expiryDate),
                                            ).format("DD MMM YYYY")
                                          : "-"}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="fs12 font-bold black">
                                        {itm?.quantity}
                                        {itm?.stockId &&
                                        itm?.uomName?.length > 0
                                          ? `${getRightPackageUom(itm?.uomName, true)}`
                                          : ""}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="fs12 font-bold black">
                                        {Number(
                                          itm?.mrpPrice ?? itm?.price,
                                        )?.toFixed(2)}
                                      </div>
                                    </td>
                                    {petShopTabList?.some(
                                      (li) => li?.discount > 0,
                                    ) ? (
                                      <td>
                                        <div className="fs12 font-bold black">
                                          {Number(
                                            itm?.mrpDiscount ?? itm?.discount,
                                          ) > 0
                                            ? Number(
                                                itm?.mrpDiscount ??
                                                  itm?.discount,
                                              )?.toFixed(2)
                                            : "-"}
                                        </div>
                                      </td>
                                    ) : null}
                                    <td>
                                      <div className="fs12 font-bold black">
                                        {clinicDet?.pharmacyTaxId?.length > 0
                                          ? itm?.tax > 0
                                            ? `${Number(itm?.tax)?.toFixed(2)}`
                                            : "-"
                                          : "-"}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="fs12 font-bold black">
                                        {Number(itm?.total)?.toFixed(2)}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                        <InvoiceBillFooter
                          billedByName={selectedBill?.billedByName ?? ""}
                          tabList={petShopTabList}
                          subtotalLabel="Subtotal"
                          disclaimer={clinicDet?.disclaimer}
                          totalPrefix="Rs. "
                          discountPrefix="Rs. "
                          usePetShopTax
                          petShopTaxId={clinicDet?.petShopTaxId}
                        />
                      </div>
                    </div>
                  </div>
                  {resolvedBillsToShow?.includes("petshop") &&
                  selectedBill?.status !== "cancelled" &&
                  Number(selectedBill?.amtPaid) > 0 &&
                  getTabBasedCollections("petshop")?.length > 0 ? (
                    <div className="billing-payment-summary-section">
                      <div className="ph10">
                        <div className="txt-semi-bold fs14 black mt30 ph10">
                          PAYMENT SUMMARY
                        </div>
                        <div className="thin-line mt10" />
                        <table className="custom-table">
                          {getPaySumTableHead()}
                          <tbody>
                            {getTabBasedCollectionsWithBalance("petshop")?.map(
                              (tl, i) => {
                                return (
                                  <tr key={`ps_${tl?.collectionId}_${i}`}>
                                    <td>
                                      {moment(new Date(tl?.receiptDate)).format(
                                        "DD MMM YYYY",
                                      )}
                                    </td>
                                    <td>
                                      {tl?.receiptNo ??
                                        selectedBill?.treatmentInvoiceNumber ??
                                        ""}
                                    </td>
                                    <td>
                                      {Number(tl?.balanceBefore ?? 0)?.toFixed(
                                        2,
                                      )}
                                    </td>
                                    <td>
                                      {tl?.paymentMode === "advanceAdjustment"
                                        ? "Advance Adjustment"
                                        : tl?.paymentMode}
                                    </td>
                                    <td>
                                      {Number(tl?.amountPaid ?? 0) > 0
                                        ? Number(tl?.amountPaid ?? 0)?.toFixed(
                                            2,
                                          )
                                        : "Nil"}
                                    </td>
                                    <td>
                                      {Number(tl?.balanceAfter ?? 0) > 0
                                        ? Number(
                                            tl?.balanceAfter ?? 0,
                                          )?.toFixed(2)
                                        : "Nil"}
                                    </td>
                                  </tr>
                                );
                              },
                            )}
                          </tbody>
                        </table>
                        <div className="thin-line" />
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>

        {billsToShow?.length > 0 &&
        treatTabList?.length + pharTabList?.length + petShopTabList?.length >
          0 ? null : (
          <div className="no-rec">No records available</div>
        )}
        {billsToShow?.length > 0 &&
        treatTabList?.length + pharTabList?.length + petShopTabList?.length >
          0 ? (
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            className="back-white"
          >
            <div className="flex-end mv20">
              <div className="flex-row-ali-cen">
                {selectedBill?.type?.toLowerCase() === "pharmacy" &&
                billsToShow?.includes("pharmacy") &&
                pharTabList?.length > 0 ? (
                  <div>
                    <CustomCheckbox
                      label="Dot Matrix (Monospace)"
                      checked={isDotMatrixPrint}
                      onChange={() => setDotMatrixPrint(!isDotMatrixPrint)}
                    />
                  </div>
                ) : null}
                {isDotMatrixPrint ||
                (selectedBill?.type === "Pharmacy" &&
                  selectedBill?.amtPaid === 0) ||
                (selectedBill?.type === "Pet Shop" &&
                  selectedBill?.amtPaid === 0) ||
                (selectedBill?.type === "Clinic" &&
                  selectedBill?.tab === "treatment" &&
                  selectedBill?.amtPaid === 0) ||
                (selectedBill?.type === "Clinic" &&
                  selectedBill?.tab === "pharmacy" &&
                  selectedBill?.amtPaid === 0) ||
                (selectedBill?.type === "Clinic" &&
                  selectedBill?.tab === "petShop" &&
                  selectedBill?.amtPaid === 0) ||
                (selectedBill?.type === "Clinic" &&
                  selectedBill?.amtPaid === 0) ||
                (selectedBill?.status ?? "") === "cancelled" ? null : (
                  <div className="mr20">
                    <CustomButton
                      text="Print Receipt"
                      onClick={handlePrintInvoice}
                      smallBtn
                    />
                  </div>
                )}
                <div className="mr20">
                  <CustomButton
                    text="Print Invoice"
                    onClick={onPrintInvoice}
                    smallBtn
                  />
                </div>
              </div>
            </div>
          </Grid>
        ) : null}
        <div style={{ display: "none" }}>
          <ContentToPrint
            ref={receiptToPrint}
            footer={
              <div className="prescription-print-footer-inner">
                <div className="mb10 prescription-print-footer-doctor-row">
                  <div className="flex-center">
                    <div className="txt-regular fs12 black">
                      Thank you for the payment
                    </div>
                  </div>
                </div>

                {clinicDet?.footer?.length > 0 ? (
                  <img src={clinicDet?.footer} alt="" />
                ) : null}
              </div>
            }
            header={
              <div className="flex-row-ali-cen mv20">
                <div className="w35Per">
                  <div>
                    {clinicDet?.image ? (
                      <img src={clinicDet?.image} className="img-h75" alt="" />
                    ) : (
                      <div className="flex-center h50img">
                        <Typography className="font-bold fs30 white-color capitalize">
                          {clinicDet?.name?.[0]}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
                <div className="w30Per flex-center">
                  <div className="font-bold fs20 blue-color">
                    Payment Receipt
                  </div>
                </div>
                <div className="w35Per flex1-end">
                  <div className="prescription-doc-header-clinic">
                    <div className="font-bold blue-color fs16">
                      {clinicDet?.name ?? ""}
                    </div>
                    <div className="txt-regular fs12 blue-color">
                      {clinicDet?.address ?? ""}
                    </div>
                    {clinicDet?.taxId?.length > 0 ? (
                      <div className="flex-row-ali-cen mt5">
                        <div className="font-bold fs12 blue-color">
                          TAX ID:{" "}
                        </div>
                        <div className="txt-regular fs12 blue-color ml3">
                          {clinicDet?.taxId ?? ""}
                        </div>
                      </div>
                    ) : null}
                    {clinicDet?.mobile?.length > 0 ? (
                      <div className="flex-row-ali-cen mt5">
                        <div className="font-bold fs12 blue-color">
                          Contact:{" "}
                        </div>
                        <div className="txt-regular fs12 blue-color ml3">
                          {clinicDet?.mobile ?? ""}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            }
          >
            <tr>
              <td className="prescription-print-tbody-cell">
                <div className="prescription-print-body-inner">
                  <div className="p5">
                    <div>
                      <div className="flex-row prescription-print-patient-block">
                        <div className="w65Per">
                          <div className="flex-column">
                            <div className="flex-row mv5">
                              {selectedBill?.userName?.length > 0 ? (
                                <>
                                  <div className="font-bold fs12 blue-color">
                                    Customer:
                                  </div>
                                  <div className="font-bold fs12 black capitalize ml5">
                                    {selectedBill?.userName}
                                  </div>
                                </>
                              ) : null}
                              {selectedBill?.petName?.length > 0 ? (
                                <>
                                  <div
                                    className={`font-bold fs12 blue-color ${selectedBill?.userName?.length > 0 ? "ml10" : ""}`}
                                  >
                                    Pet Name:
                                  </div>
                                  <div className="font-bold fs12 black capitalize ml5">
                                    {selectedBill?.petName}
                                  </div>
                                </>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {selectedBill?.doctorName?.length > 0 ? (
                          <div className="w35Per">
                            <div className="flex-column">
                              <div className="flex-row mt10">
                                <div className="font-bold fs12 blue-color">
                                  Vet Name:
                                </div>
                                <div className="font-bold fs12 black ml5 capitalize">
                                  {`${getRightSaluation(clinicVets, selectedBill?.doctorId)}${
                                    selectedBill?.doctorName ?? ""
                                  }`}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <div className="box-hor-split mv10" />
                      <table className="custom-table">
                        <thead>
                          <tr>
                            <th className="w10Per">Receipt Date</th>
                            <th className="w15Per">Receipt No.</th>
                            <th className="w10Per">Invoice Date</th>
                            <th className="w15Per">Invoice No.</th>
                            <th className="w15Per">Type</th>
                            <th className="w20Per">Payment Mode</th>
                            <th className="w15Per">Amount Paid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            // billsToShow?.includes("treatment") &&
                            Number(selectedBill?.amtPaid) > 0 &&
                            getTabBasedCollections("treatment")?.length > 0
                              ? getTabBasedCollections("treatment")?.map(
                                  (li, i) => (
                                    <tr key={`${li?.collectionId}_tre_${i}`}>
                                      <td>
                                        {moment(
                                          new Date(li?.receiptDate),
                                        ).format("DD MMM YYYY")}
                                      </td>
                                      <td>{li?.receiptNo ?? ""}</td>
                                      <td>
                                        {moment(
                                          new Date(selectedBill?.createdDate),
                                        ).format("DD MMM YYYY")}
                                      </td>
                                      <td>{li?.invoiceNo ?? ""}</td>
                                      <td>{li?.collectionType ?? ""}</td>
                                      <td>
                                        {li?.paymentMode === "advanceAdjustment"
                                          ? "Advance Adjustment"
                                          : li?.paymentMode}
                                      </td>
                                      <td>
                                        {Number(li?.amountPaid ?? 0) > 0
                                          ? Number(
                                              li?.amountPaid ?? 0,
                                            )?.toFixed(2)
                                          : "Nil"}
                                      </td>
                                    </tr>
                                  ),
                                )
                              : null
                          }
                          {/* {billsToShow?.includes("pharmacy") &&
                          Number(selectedBill?.amtPaid) > 0 &&
                          getTabBasedCollections("pharmacy")?.length > 0
                            ? getTabBasedCollections("pharmacy")?.map(
                                (li, i) => (
                                  <tr key={`${li?.collectionId}_phar_${i}`}>
                                    <td>
                                      {moment(new Date(li?.receiptDate)).format(
                                        "DD MMM YYYY",
                                      )}
                                    </td>
                                    <td>{li?.receiptNo ?? ""}</td>
                                    <td>
                                      {moment(
                                        new Date(selectedBill?.createdDate),
                                      ).format("DD MMM YYYY")}
                                    </td>
                                    <td>{li?.invoiceNo ?? ""}</td>
                                    <td>{li?.collectionType ?? ""}</td>
                                    <td>
                                      {li?.paymentMode === "advanceAdjustment"
                                        ? "Advance Adjustment"
                                        : li?.paymentMode}
                                    </td>
                                    <td>
                                      {Number(li?.amountPaid ?? 0) > 0
                                        ? Number(li?.amountPaid ?? 0)?.toFixed(
                                            2,
                                          )
                                        : "Nil"}
                                    </td>
                                  </tr>
                                ),
                              )
                            : null}
                          {billsToShow?.includes("petshop") &&
                          Number(selectedBill?.amtPaid) > 0 &&
                          getTabBasedCollections("petshop")?.length > 0
                            ? getTabBasedCollections("petshop")?.map(
                                (li, i) => (
                                  <tr key={`${li?.collectionId}_ps_${i}`}>
                                    <td>
                                      {moment(new Date(li?.receiptDate)).format(
                                        "DD MMM YYYY",
                                      )}
                                    </td>
                                    <td>{li?.receiptNo ?? ""}</td>
                                    <td>
                                      {moment(
                                        new Date(selectedBill?.createdDate),
                                      ).format("DD MMM YYYY")}
                                    </td>
                                    <td>{li?.invoiceNo ?? ""}</td>
                                    <td>{li?.collectionType ?? ""}</td>
                                    <td>
                                      {li?.paymentMode === "advanceAdjustment"
                                        ? "Advance Adjustment"
                                        : li?.paymentMode}
                                    </td>
                                    <td>
                                      {Number(li?.amountPaid ?? 0) > 0
                                        ? Number(li?.amountPaid ?? 0)?.toFixed(
                                            2,
                                          )
                                        : "Nil"}
                                    </td>
                                  </tr>
                                ),
                              )
                            : null} */}
                        </tbody>
                      </table>
                      <div className="thin-line" />
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </ContentToPrint>
        </div>
      </div>
    </CustomModal>
  );
};

export default BillingPreview;
